import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { excludeCompanyLevel, isCompanyLevelBrand } from '../../utils/brandFilters';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  TablePagination,
  Stack,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Divider,
  Menu,
  ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Business as BusinessIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  TrendingUp as InIcon,
  TrendingDown as OutIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  MoreVert as MoreVertIcon,
  DeleteOutline as DisposeIcon
} from '@mui/icons-material';
import {
  getCalculatedStockLevels,
  getStockSummary,
  getSkuBatches,
  getBulkSkuBatches,
  getWarehouses,
  receiveBatch,
  bulkImportBatches,
  downloadCSVTemplate,
  createBatchAdjustment,
  updateBatch,
  adjustWarehouseStock,
  setTrueStock,
  createDisposal,
  exportStockLevels,
  exportStockLevelsDetailed,
  formatCurrency,
  formatNumber,
  formatDate
} from '../../services/inventoryService';
import api from '../../config/api';
import StockBreakdownDialog from './StockBreakdownDialog';
import { getExpiryInfo, describeDaysLeft, HOLD_SOURCE_LABEL, EXPIRING_SOON_DAYS } from './stockExpiry';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Base URL for direct api calls that aren't yet wrapped in inventoryService
// (the inbound-detail endpoint for the Inbound hover tooltip).
const STOCK_API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
// The batches endpoints answer this code when a past date's stock could not be read (server busy, DT-892)
const PAST_DATE_STOCK_UNAVAILABLE = 'PAST_DATE_STOCK_UNAVAILABLE';
const STOCK_READ_BUSY_MESSAGE = 'Stock for this date could not be read right now. Please try again.';

function StockLevels() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrand, brands, selectBrand } = useBrand();

  // "All Brands" consolidated view — the summary tiles show CONSOLIDATED company-wide
  // totals (from the daily snapshot) and the per-SKU table is not shown (pick a real
  // brand to drill into SKUs). The legacy synthetic "Company Level" brand is no longer
  // selectable, but if it is still the persisted selection we treat it as All Brands.
  const [allBrandsMode, setAllBrandsMode] = useState(searchParams.get('brand') === 'all');
  const isAllBrands = allBrandsMode || isCompanyLevelBrand(selectedBrand);

  // Read initial values from URL query parameters
  const initialBrandId = searchParams.get('brand') ? parseInt(searchParams.get('brand'), 10) : null;
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const initialFilter = searchParams.get('filter') || 'all';
  const initialWarehouse = searchParams.get('warehouse') || 'all';
  const initialSearch = searchParams.get('search') || '';
  const initialSortField = searchParams.get('sortBy') || 'total_quantity';
  const initialSortDir = searchParams.get('sortDir') || 'desc';
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialRows = parseInt(searchParams.get('rows') || '50', 10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data from new calculated stock levels API
  const [stockItems, setStockItems] = useState([]);
  const [stockSummary, setStockSummary] = useState({ internal: [], external: [] });
  const [warehouses, setWarehouses] = useState([]);

  // Configuration warnings from API
  const [configWarnings, setConfigWarnings] = useState([]);

  // Batch details loaded on demand
  const [batchDetails, setBatchDetails] = useState({});
  // sku -> message when that date's stock could not be read (server busy, DT-892): shown with a retry, not as "no batches"
  const [batchErrors, setBatchErrors] = useState({});
  // Inbound (incoming-stock) line detail for the per-warehouse "Inbound" hover
  // tooltip. Keyed by sku → { warehouse_code → [ {source_type, reference, eta,
  // origin, quantity} ] }. Lazy-loaded when a SKU row is expanded.
  const [incomingDetails, setIncomingDetails] = useState({});
  const [loadingIncoming, setLoadingIncoming] = useState({});
  const [loadingBatches, setLoadingBatches] = useState({});
  const bulkRunRef = useRef(0);      // generation of the latest bulk batch run (DT-910)
  const bulkOwnerRef = useRef({});   // sku -> the bulk run that set its loading flag (DT-910)
  const batchContextRef = useRef(0); // bumped when the as-of date / brand changes; older batch answers are dropped (DT-910)
  // What each 3PL reports as expired / on hold, plus warehouse lot expiry dates
  // ({ holds, lots } from /api/stock-levels/warehouse-expiry). Feeds the Expiry
  // and Sellable columns; on-hand itself is unchanged.
  const [warehouseExpiry, setWarehouseExpiry] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [warehouseFilter, setWarehouseFilter] = useState(initialWarehouse);
  const [stockFilter, setStockFilter] = useState(initialFilter);

  // Sorting
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // Pagination
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);

  // Helper to update URL parameters
  const updateUrlParams = useCallback((updates) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      Object.entries(updates).forEach(([key, value]) => {
        // Remove default values from URL to keep it clean
        const isDefault = (
          (key === 'tab' && (value === 0 || value === '0')) ||
          (key === 'filter' && value === 'all') ||
          (key === 'warehouse' && value === 'all') ||
          (key === 'sortBy' && value === 'total_quantity') ||
          (key === 'sortDir' && value === 'desc') ||
          (key === 'page' && (value === 0 || value === '0')) ||
          (key === 'rows' && (value === 50 || value === '50')) ||
          (key === 'search' && value === '') ||
          value === null || value === undefined
        );
        if (isDefault) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Track if we've initialized from URL (to prevent overwriting URL on mount)
  const initializedFromUrl = useRef(false);

  // Sync brand from URL on mount - URL takes priority over context
  useEffect(() => {
    if (initialBrandId && brands.length > 0 && !initializedFromUrl.current) {
      const brandFromUrl = brands.find(b => b.id === initialBrandId);
      if (brandFromUrl) {
        initializedFromUrl.current = true;
        selectBrand(brandFromUrl);
      }
    }
  }, [initialBrandId, brands, selectBrand]);

  // Debounced search URL update
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPage(0);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      updateUrlParams({ search: value, page: 0 });
    }, 500);
  }, [updateUrlParams]);

  // Expanded rows for warehouse/batch details
  const [expandedRows, setExpandedRows] = useState({});

  // Receive inventory dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  // Guards the Receive button against a double-submit — each submit creates a new
  // batch + counted adjustment, so a double-click would double-count on-hand.
  const [receiving, setReceiving] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    sku: '',
    warehouse_id: '',
    batch_number: '',
    quantity: '',
    unit_cost: '',
    expiry_date: ''
  });

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // Export options dialog — lets the user pick exactly what to export
  // (level / status / in-stock / warehouse) instead of dumping the whole catalog.
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMode, setExportMode] = useState('summary'); // 'summary' | 'detailed'
  const [exportLevel, setExportLevel] = useState([]);      // multi-select; [] = all
  const [exportStatus, setExportStatus] = useState([]);    // multi-select; [] = all
  const [exportInStock, setExportInStock] = useState('all'); // 'all' | 'instock'
  const [exportWarehouse, setExportWarehouse] = useState([]); // multi-select; [] = all
  const [exportIncludeVariance, setExportIncludeVariance] = useState(false); // off = fast

  const handleExportConfirm = async () => {
    if (!selectedBrand?.id) { setError('Please select a brand to export'); return; }
    const opts = {
      level: exportLevel,
      status: exportStatus,
      inStock: exportInStock === 'instock',
      warehouse: exportWarehouse,
      includeVariance: exportIncludeVariance,
    };
    const detailed = exportMode === 'detailed';
    // Close the dialog immediately so the page is NOT blocked while the file builds.
    // The download runs in the background; a toast reports the result.
    setExportDialogOpen(false);
    setError(null);
    setSuccess('Export started — your download will begin when it is ready…');
    try {
      if (detailed) {
        await exportStockLevelsDetailed(selectedBrand.id, asOfDate, opts);
        setSuccess('Detailed stock levels exported');
      } else {
        await exportStockLevels(selectedBrand.id, asOfDate, opts);
        setSuccess('Stock levels exported');
      }
    } catch (err) {
      setError(err.message || 'Failed to export stock levels');
    }
  };
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Point-in-time date selector (defaults to today)
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  // Movement breakdown popup
  const [movementPopup, setMovementPopup] = useState({
    open: false,
    sku: '',
    productName: '',
    warehouseCode: '',
    warehouseData: null
  });

  // Batch adjustment dialog
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    batch: null,
    primarySku: '',
    adjustment_quantity: '',
    adjustment_type: 'manual',
    reason: ''
  });
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);

  // Edit batch dialog (for batch_number, expiry_date, unit_cost, and quantity adjustment)
  const [editBatchDialogOpen, setEditBatchDialogOpen] = useState(false);
  const [editBatchForm, setEditBatchForm] = useState({
    batch: null,
    primarySku: '',
    batch_number: '',
    expiry_date: '',
    unit_cost: '',
    new_quantity: '',
    adjustment_reason: 'Stock count'
  });
  const [editBatchLoading, setEditBatchLoading] = useState(false);

  // Batch actions menu (Edit / Dispose)
  const [batchMenuAnchor, setBatchMenuAnchor] = useState(null);
  const [batchMenuContext, setBatchMenuContext] = useState({ batch: null, primarySku: '' });

  // Dispose batch dialog
  const [disposeBatchDialogOpen, setDisposeBatchDialogOpen] = useState(false);
  const [disposeBatchForm, setDisposeBatchForm] = useState({
    batch: null,
    primarySku: '',
    quantity: '',
    disposal_type: 'damaged',
    reason: '',
    disposal_date: new Date().toISOString().split('T')[0]
  });
  const [disposeBatchLoading, setDisposeBatchLoading] = useState(false);

  // Edit warehouse stock dialog (for warehouses with 0 batches - creates system batch)
  const [editWarehouseDialogOpen, setEditWarehouseDialogOpen] = useState(false);
  const [editWarehouseForm, setEditWarehouseForm] = useState({
    sku: '',
    warehouse_code: '',
    warehouse_data: null,  // Current warehouse data (qty, avg_cost, etc.)
    primarySku: '',
    new_quantity: '',
    unit_cost: '',
    expiry_date: '',
    batch_number: '',
    adjustment_reason: 'Stock count adjustment'
  });
  const [editWarehouseLoading, setEditWarehouseLoading] = useState(false);

  // Tab state: 0 = Products (L1 or prepacked L2), 1 = Parts (L0 raw components only), 2 = All
  //   Sellable L2 bundles are virtual combos (not physical, not L0) → shown only under "All".
  const [activeTab, setActiveTab] = useState(initialTab >= 0 && initialTab <= 2 ? initialTab : 0);


  useEffect(() => {
    // Clear batch details when date changes (will be reloaded on demand). Reads still in flight belong to the old
    // date/brand: bumping the context makes them drop their answers, and their loading flags are cleared here (DT-910).
    // Old bulk runs no longer own any SKU either, so a row's own read clears its loader and an old run ending late
    // clears nothing (DT-1040).
    batchContextRef.current += 1;
    bulkOwnerRef.current = {};
    setBatchDetails({});
    setBatchErrors({});
    setLoadingBatches({});
    // The Inbound hover breakdown is brand-scoped and cached per SKU: a SKU sold under two brands would
    // otherwise keep showing the previous brand's PO / transfer lines (DT-990).
    setIncomingDetails({});
    setLoadingIncoming({});

    // The Stock page is often opened via a URL that pins a specific brand
    // (e.g. /inventory/stock?brand=10 from the Inventory dashboard). The brand
    // actually fetched below comes from BrandContext.selectedBrand, which is
    // seeded from localStorage — the brand THIS BROWSER last viewed, which may be
    // a different brand than the URL asks for (and, on a shared machine, even a
    // different user's last brand). The URL is reconciled into the context by the
    // "sync brand from URL" effect above, but that runs AFTER this effect on
    // mount, so without this guard we fire a fetch for the stale cached brand and
    // flash it before correcting to the URL brand.
    //
    // So: if the URL pins a brand that (a) this user is allowed to see (present in
    // the per-user `brands` list — while it is still loading, treat as "maybe")
    // and (b) the context hasn't adopted yet, skip this run and let the sync
    // effect apply it; selectedBrand will then match and we load exactly once.
    // If the pinned brand is NOT in the user's brand list, fall through to the
    // normal load (their own brand) rather than hang.
    const urlPinnedId = Number.isInteger(initialBrandId) ? initialBrandId : null;
    const urlBrandStillResolvable =
      urlPinnedId != null && (brands.length === 0 || brands.some(b => b.id === urlPinnedId));
    if (!isAllBrands && urlPinnedId && selectedBrand?.id !== urlPinnedId && urlBrandStillResolvable) {
      return;
    }

    // Load for All Brands (consolidated) or when a specific brand is selected.
    if (isAllBrands || selectedBrand?.id) {
      loadData();
    } else {
      // Clear data if no brand is selected (and drop any load still in flight)
      loadSeqRef.current += 1;
      setLoading(false);
      setStockItems([]);
      setStockSummary({ internal: [], external: [] });
    }
  }, [selectedBrand?.id, asOfDate, isAllBrands, initialBrandId, brands]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    if (brandId === 'all') {
      setAllBrandsMode(true);
      updateUrlParams({ brand: 'all' });
      return;
    }
    setAllBrandsMode(false);
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
      updateUrlParams({ brand: brandId });
    }
  };

  // A brand or as-of date change starts a new load while the old one may still be in flight; if the old
  // answer arrived last it replaced the new brand's list (DT-931). Each load claims a sequence number and
  // only the latest may touch state (same guard as InventoryMovements.js).
  const loadSeqRef = useRef(0);

  const loadData = async (forceRefresh = false) => {
    // Guard: need either All Brands mode or a specific brand.
    if (!isAllBrands && !selectedBrand?.id) {
      console.warn('loadData called without selectedBrand - skipping');
      return;
    }

    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = {
        brand_id: isAllBrands ? 'all' : selectedBrand.id
      };
      if (asOfDate) {
        params.as_of_date = asOfDate;
      }
      if (forceRefresh) {
        params.force_refresh = true;
      }

      // All Brands shows only CONSOLIDATED summary tiles — skip the heavy per-SKU
      // table call (pick a specific brand to see per-SKU stock).
      const [stockRes, summaryRes, warehousesRes] = await Promise.all([
        isAllBrands
          ? Promise.resolve({ data: { items: [] }, warnings: [] })
          : getCalculatedStockLevels({ ...params, limit: 1000 }),
        getStockSummary(params).catch(err => {
          console.warn('Failed to load stock summary:', err);
          return { data: { internal: [], external: [] } };
        }),
        getWarehouses({ is_active: true })
      ]);
      if (seq !== loadSeqRef.current) return;

      const items = stockRes?.data?.items || [];
      setStockItems(items);
      setStockSummary(summaryRes?.data || { internal: [], external: [] });
      // Backend returns { success, warehouses, total, limit, offset }
      setWarehouses(warehousesRes?.warehouses || []);

      // Extract configuration warnings from the response
      setConfigWarnings(stockRes?.warnings || []);
    } catch (err) {
      console.error('Failed to load stock data:', err);
      if (seq === loadSeqRef.current) setError('Failed to load stock data. Please try again.');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  // Load batch details for a SKU on demand
  const loadBatchDetails = async (sku, warehouseCode = null, force = false) => {
    const key = warehouseCode ? `${sku}|${warehouseCode}` : sku;

    if (!force && (batchDetails[key] || loadingBatches[key])) return;

    setLoadingBatches(prev => ({ ...prev, [key]: true }));
    const ctx = batchContextRef.current;

    try {
      // include_transfer_lots: name transferred-in stock by its transfer lots (with expiry)
      // instead of one 'Other stock' line — display only.
      const params = { as_of_date: asOfDate, include_transfer_lots: 1 };
      if (warehouseCode) {
        params.warehouse_code = warehouseCode;
      }
      if (selectedBrand?.id) {
        params.brand_id = selectedBrand.id;
      }
      const res = await getSkuBatches(sku, params);
      if (batchContextRef.current !== ctx) return;   // the date/brand changed meanwhile: an old-date answer (DT-910)
      setBatchDetails(prev => ({ ...prev, [key]: res?.data?.batches || [] }));
      setBatchErrors(prev => ({ ...prev, [key]: null }));
    } catch (err) {
      console.error('Failed to load batch details:', err);
      if (batchContextRef.current !== ctx) return;
      if (err?.response?.data?.code === PAST_DATE_STOCK_UNAVAILABLE) {
        setBatchErrors(prev => ({ ...prev, [key]: err.response.data.error || STOCK_READ_BUSY_MESSAGE }));
      }
    } finally {
      // Not after a date/brand change (that reset the flags), and not when a bulk run has taken the SKU over
      if (batchContextRef.current === ctx && bulkOwnerRef.current[key] == null) {
        setLoadingBatches(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  // Lazy-load the inbound (incoming-stock) line breakdown for a SKU so the
  // per-warehouse "Inbound" cell can show a hover tooltip (PO# / Transfer# +
  // qty + ETA + origin). Loaded on row-expand, same as batch details, so the
  // hover is instant. Brand-scoped; needs selectedBrand to resolve the SKU.
  const loadIncomingDetails = async (sku, force = false) => {
    if (!sku || !selectedBrand?.id) return;
    if (!force && (incomingDetails[sku] || loadingIncoming[sku])) return;
    setLoadingIncoming(prev => ({ ...prev, [sku]: true }));
    const ctx = batchContextRef.current;
    try {
      const res = await api.get(
        `${STOCK_API_BASE}/api/stock-levels/sku/${encodeURIComponent(sku)}/incoming`,
        { params: { brand_id: selectedBrand.id } }
      );
      if (batchContextRef.current !== ctx) return;   // the brand/date changed meanwhile: the old brand's lines (DT-990)
      setIncomingDetails(prev => ({ ...prev, [sku]: res?.data?.data?.by_warehouse || {} }));
    } catch (err) {
      console.error('Failed to load inbound details:', err);
      if (batchContextRef.current !== ctx) return;
      setIncomingDetails(prev => ({ ...prev, [sku]: {} }));
    } finally {
      if (batchContextRef.current === ctx) setLoadingIncoming(prev => ({ ...prev, [sku]: false }));
    }
  };

  // Warehouse-reported expired / on-hold stock for the selected brand.
  useEffect(() => {
    if (isAllBrands || !selectedBrand?.id) { setWarehouseExpiry(null); return undefined; }
    let cancelled = false;
    api.get(`${STOCK_API_BASE}/api/stock-levels/warehouse-expiry`, { params: { brand_id: selectedBrand.id } })
      .then(res => { if (!cancelled) setWarehouseExpiry(res?.data?.data || { holds: [], lots: [] }); })
      .catch(err => {
        console.error('Failed to load warehouse expiry:', err);
        if (!cancelled) setWarehouseExpiry({ holds: [], lots: [] });
      });
    return () => { cancelled = true; };
  }, [selectedBrand?.id, isAllBrands]);

  // Auto-load batch details for all SKUs via bulk endpoint (for accurate expired/expiring badges)
  // Single API call instead of individual per-SKU calls — all badges appear together in ~3-5s
  useEffect(() => {
    if (!stockItems || stockItems.length === 0) return;

    let cancelled = false;
    // DT-910: each bulk run owns the loading flags it set (bulkOwnerRef: sku -> run). A run cancelled mid-request (the
    // stock list changed, e.g. a new as-of date) used to leave its flags set for good, so the next run skipped those
    // SKUs and an expand returned early. A newer run now takes over SKUs held by an older run, and every run, cancelled
    // or not, clears the flags it still owns when it ends. Flags set by a single-row read (loadBatchDetails) are never
    // owned by a bulk run and are left alone, as before.
    const run = ++bulkRunRef.current;
    const ctx = batchContextRef.current;
    // stale: superseded by a newer stock list (cancelled) or the date/brand changed before that list arrived
    const stale = () => cancelled || batchContextRef.current !== ctx;
    const heldByOlderRun = (sku) => bulkOwnerRef.current[sku] != null && bulkOwnerRef.current[sku] < run;

    const loadAllBatchesBulk = async () => {
      // Collect all SKUs that need loading
      const skusToLoad = [];
      for (const item of stockItems) {
        const sku = item.primary_sku || item.parent_sku;
        if (sku && !batchDetails[sku] && (!loadingBatches[sku] || heldByOlderRun(sku))) {
          skusToLoad.push(sku);
        }
      }

      if (skusToLoad.length === 0) return;
      for (const sku of skusToLoad) bulkOwnerRef.current[sku] = run;

      // Mark all as loading
      setLoadingBatches(prev => {
        const next = { ...prev };
        for (const sku of skusToLoad) next[sku] = true;
        return next;
      });

      // Accumulates successfully-fetched chunks; declared outside the try so the
      // catch fallback can keep the chunks that already succeeded.
      const merged = {};
      try {
        const params = {};
        if (asOfDate) params.as_of_date = asOfDate;
        if (selectedBrand?.id) params.brand_id = selectedBrand.id;

        // Backend caps bulk-batches at 100 SKUs per request (memory-pressure guard),
        // so chunk the SKU list rather than sending all of them at once (was 400ing).
        const CHUNK_SIZE = 100;
        for (let i = 0; i < skusToLoad.length; i += CHUNK_SIZE) {
          if (stale()) return;
          const chunk = skusToLoad.slice(i, i + CHUNK_SIZE);
          const res = await getBulkSkuBatches(chunk, params);
          if (res?.success && res?.data) {
            Object.assign(merged, res.data);
          }
        }

        if (stale()) return;

        // A SKU whose date could not be read (DT-892) is left unloaded with a message, not shown as zero batches
        const unread = skusToLoad.filter(sku => merged[sku]?.code === PAST_DATE_STOCK_UNAVAILABLE);
        setBatchDetails(prev => {
          const next = { ...prev };
          for (const sku of skusToLoad) {
            const skuData = merged[sku];
            if (skuData?.code !== PAST_DATE_STOCK_UNAVAILABLE) next[sku] = skuData?.batches || [];
          }
          return next;
        });
        setBatchErrors(prev => {
          const next = { ...prev };
          for (const sku of skusToLoad) next[sku] = null;
          for (const sku of unread) next[sku] = merged[sku].error || STOCK_READ_BUSY_MESSAGE;
          return next;
        });
      } catch (err) {
        console.error('Failed to load bulk batch details, falling back to individual:', err);
        if (!stale()) {
          // Commit the chunks that already succeeded so a mid-list chunk failure
          // doesn't discard chunks 1..N-1, then only refetch the SKUs that never
          // made it into `merged` (previously refetched ALL SKUs one-by-one).
          if (Object.keys(merged).length > 0) {
            setBatchDetails(prev => {
              const next = { ...prev };
              for (const sku of Object.keys(merged)) {
                if (merged[sku]?.code !== PAST_DATE_STOCK_UNAVAILABLE) next[sku] = merged[sku]?.batches || [];
              }
              return next;
            });
            setBatchErrors(prev => {
              const next = { ...prev };
              for (const sku of Object.keys(merged)) {
                next[sku] = merged[sku]?.code === PAST_DATE_STOCK_UNAVAILABLE ? (merged[sku].error || STOCK_READ_BUSY_MESSAGE) : null;
              }
              return next;
            });
          }
          const remaining = skusToLoad.filter(sku => !Object.prototype.hasOwnProperty.call(merged, sku));
          for (let i = 0; i < remaining.length; i += 5) {
            if (stale()) return;
            const chunk = remaining.slice(i, i + 5);
            // force: this run's own loading flag must not make the single read return early
            await Promise.all(chunk.map(sku => loadBatchDetails(sku, null, true)));
          }
        }
      } finally {
        // Clear only the flags this run still owns, even when it was cancelled (a newer run may have taken some over)
        const owned = skusToLoad.filter(sku => bulkOwnerRef.current[sku] === run);
        for (const sku of owned) delete bulkOwnerRef.current[sku];
        if (owned.length > 0) {
          setLoadingBatches(prev => {
            const next = { ...prev };
            for (const sku of owned) next[sku] = false;
            return next;
          });
        }
      }
    };

    loadAllBatchesBulk();

    return () => { cancelled = true; };
  }, [stockItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open batch adjustment dialog
  const openAdjustmentDialog = (batch, primarySku) => {
    setAdjustmentForm({
      batch,
      primarySku,
      adjustment_quantity: '',
      adjustment_type: 'manual',
      reason: ''
    });
    setError(null); setAdjustmentDialogOpen(true);
  };

  // Open edit batch dialog
  const openEditBatchDialog = (batch, primarySku) => {
    // If batch has no unit_cost, find the last batch's COGS for the same warehouse/SKU
    let defaultUnitCost = '';
    if (batch.unit_cost !== null && batch.unit_cost !== undefined && batch.unit_cost > 0) {
      defaultUnitCost = batch.unit_cost;
    } else {
      // Find batches for this SKU/warehouse combination, sorted by receipt_date descending
      const skuBatches = batchDetails[primarySku] || [];
      const warehouseBatches = skuBatches
        .filter(b => b.warehouse_code === batch.warehouse_code && b.unit_cost > 0)
        .sort((a, b) => new Date(b.receipt_date) - new Date(a.receipt_date));

      if (warehouseBatches.length > 0) {
        defaultUnitCost = warehouseBatches[0].unit_cost;
      }
    }

    setEditBatchForm({
      batch,
      primarySku,
      batch_number: batch.batch_number || '',
      expiry_date: batch.expiry_date ? batch.expiry_date.split('T')[0] : '',
      unit_cost: defaultUnitCost,
      new_quantity: '',
      adjustment_reason: 'Stock count'
    });
    setError(null); setEditBatchDialogOpen(true);
  };

  // Submit edit batch
  const handleSubmitEditBatch = async () => {
    if (!editBatchForm.batch) {
      setError('No batch selected');
      return;
    }

    // Calculate adjustment from new_quantity (if provided)
    const currentQty = editBatchForm.batch.current_quantity || 0;
    const newQty = editBatchForm.new_quantity !== '' ? parseFloat(editBatchForm.new_quantity) : null;
    const adjustmentQty = newQty !== null ? newQty - currentQty : 0;
    const hasAdjustment = adjustmentQty !== 0;

    // Validate: if adjustment is needed, reason is required
    if (hasAdjustment && !editBatchForm.adjustment_reason) {
      setError('Please provide a reason for the quantity adjustment');
      return;
    }

    setEditBatchLoading(true);
    try {
      // Update batch info (batch_number, expiry_date, unit_cost)
      await updateBatch(editBatchForm.batch.id, {
        batch_number: editBatchForm.batch_number,
        expiry_date: editBatchForm.expiry_date || null,
        unit_cost: editBatchForm.unit_cost !== '' ? parseFloat(editBatchForm.unit_cost) : null
      });

      // If quantity adjustment was calculated, create the adjustment
      if (hasAdjustment) {
        await createBatchAdjustment({
          source_batch_id: editBatchForm.batch.id,
          adjustment_quantity: adjustmentQty,
          adjustment_type: 'manual',
          reason: editBatchForm.adjustment_reason
        });
      }

      const successMsg = hasAdjustment
        ? `Batch updated. Quantity adjusted from ${formatNumber(currentQty)} to ${formatNumber(newQty)} (${adjustmentQty > 0 ? '+' : ''}${formatNumber(adjustmentQty)})`
        : `Batch ${editBatchForm.batch_number || 'info'} updated successfully`;
      setSuccess(successMsg);
      setEditBatchDialogOpen(false);

      // Refresh batch details for this SKU
      const key = editBatchForm.primarySku;
      setBatchDetails(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      // Reload batch details
      setTimeout(() => loadBatchDetails(editBatchForm.primarySku, null, true), 100);

      // If adjustment was made, reload stock data to reflect new totals
      if (hasAdjustment) {
        loadData();
      }
    } catch (err) {
      console.error('Failed to update batch:', err);
      setError(err.response?.data?.error || 'Failed to update batch');
    } finally {
      setEditBatchLoading(false);
    }
  };

  // Open dispose batch dialog
  const openDisposeBatchDialog = (batch, primarySku) => {
    setDisposeBatchForm({
      batch,
      primarySku,
      quantity: '',
      disposal_type: batch.is_expired ? 'expired' : 'damaged',
      reason: '',
      disposal_date: new Date().toISOString().split('T')[0]
    });
    setError(null); setDisposeBatchDialogOpen(true);
  };

  // Submit dispose batch
  const handleSubmitDisposeBatch = async () => {
    if (!disposeBatchForm.batch) return;
    const qty = parseInt(disposeBatchForm.quantity);
    if (!qty || qty <= 0) {
      setError('Please enter a valid quantity to dispose');
      return;
    }
    if (qty > (disposeBatchForm.batch.current_quantity || 0)) {
      setError('Dispose quantity cannot exceed current quantity');
      return;
    }

    setDisposeBatchLoading(true);
    try {
      await createDisposal({
        sku: disposeBatchForm.batch.sku,
        warehouse_code: disposeBatchForm.batch.warehouse_code,
        batch_number: disposeBatchForm.batch.batch_number || '',
        quantity: qty,
        disposal_type: disposeBatchForm.disposal_type,
        disposal_date: disposeBatchForm.disposal_date,
        reason: disposeBatchForm.reason || `Disposed from batch view`,
        brand_id: selectedBrand?.id || 0
      });

      setSuccess(`Disposed ${formatNumber(qty)} units of ${disposeBatchForm.batch.sku} from ${disposeBatchForm.batch.warehouse_code}`);
      setDisposeBatchDialogOpen(false);

      // Refresh batch details and stock data
      const key = disposeBatchForm.primarySku;
      setBatchDetails(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      setTimeout(() => loadBatchDetails(disposeBatchForm.primarySku, null, true), 100);
      loadData();
    } catch (err) {
      console.error('Failed to dispose batch:', err);
      setError(err.response?.data?.error || 'Failed to record disposal');
    } finally {
      setDisposeBatchLoading(false);
    }
  };

  // Open warehouse edit dialog
  const openEditWarehouseDialog = (wh, primarySku, item) => {
    // Get batches for this warehouse to determine default unit cost
    const warehouseBatches = (batchDetails[primarySku] || [])
      .filter(b => b.warehouse_code === wh.warehouse_code && b.unit_cost > 0)
      .sort((a, b) => new Date(b.receipt_date) - new Date(a.receipt_date));

    const defaultUnitCost = warehouseBatches.length > 0
      ? warehouseBatches[0].unit_cost
      : (wh.avg_unit_cost || '');

    setEditWarehouseForm({
      sku: primarySku,
      warehouse_code: wh.warehouse_code,
      warehouse_data: wh,
      primarySku,
      product_name: item?.product_name || primarySku,
      new_quantity: wh.quantity || 0,
      unit_cost: defaultUnitCost,
      expiry_date: '',
      batch_number: `ADJ-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`,
      adjustment_reason: 'Stock count adjustment',
      effective_date: new Date().toISOString().slice(0, 10)
    });
    setError(null); setEditWarehouseDialogOpen(true);
  };

  // Submit warehouse edit (creates system batch or adjustment)
  const handleSubmitEditWarehouse = async () => {
    const { sku, warehouse_code, warehouse_data, new_quantity, unit_cost, expiry_date, batch_number, adjustment_reason, primarySku, effective_date } = editWarehouseForm;

    if (!sku || !warehouse_code) {
      setError('SKU and warehouse are required');
      return;
    }

    const currentQty = warehouse_data?.quantity || 0;
    const newQty = new_quantity !== '' ? parseFloat(new_quantity) : currentQty;
    const qtyDiff = newQty - currentQty;
    const today = new Date().toISOString().slice(0, 10);
    const isHistorical = effective_date && effective_date < today;

    // Nothing changed
    if (qtyDiff === 0 && !unit_cost && !expiry_date && !isHistorical) {
      setEditWarehouseDialogOpen(false);
      return;
    }

    setEditWarehouseLoading(true);
    try {
      if (isHistorical) {
        // Historical date: use inventory_adjustments path (set-true-stock)
        const result = await setTrueStock({
          sku,
          warehouse_code,
          effective_date,
          true_quantity: newQty,
          brand_id: selectedBrand?.id,
          reason: adjustment_reason || 'Historical stock correction'
        });

        let msg = result.data?.variance
          ? `Historical adjustment on ${effective_date}: ${sku} @ ${warehouse_code} (${result.data.variance > 0 ? '+' : ''}${formatNumber(result.data.variance)})`
          : result.message || 'No adjustment needed';
        if (result?.data?.current_quantity_resolved === false) {
          msg += ' — WARNING: existing stock for this SKU could not be confirmed, so the full quantity was booked as the variance. Verify the resulting quantity.';
        }
        setSuccess(msg);
      } else {
        // Today: GRN path. `new_quantity` is the absolute target — the backend books the
        // difference against its own current stock (an increase becomes a goods receipt,
        // a decrease becomes an inventory_adjustments variance).
        const result = await adjustWarehouseStock({
          sku,
          warehouse_code,
          brand_id: selectedBrand?.id,
          new_quantity: newQty,
          unit_cost: unit_cost !== '' ? parseFloat(unit_cost) : null,
          expiry_date: expiry_date || null,
          batch_number: batch_number || null,
          reason: adjustment_reason || 'Stock adjustment'
        });

        // Report what the SERVER actually booked, not the dialog's local guess — the two
        // differ when stock moved between opening the dialog and submitting it.
        const bookedDelta = result?.data?.delta;
        let msg = (bookedDelta === 0 || bookedDelta === undefined) && qtyDiff === 0
          ? (result?.message || `No stock change recorded at ${warehouse_code}`)
          : `Stock adjusted: ${currentQty} → ${newQty} (${(bookedDelta ?? qtyDiff) > 0 ? '+' : ''}${formatNumber(bookedDelta ?? qtyDiff)}) at ${warehouse_code}`;
        if (result?.data?.current_quantity_resolved === false) {
          msg += ' — WARNING: existing stock for this SKU could not be confirmed, so the full target was booked as a new receipt. Verify the resulting quantity.';
        }
        setSuccess(msg);
      }

      setEditWarehouseDialogOpen(false);

      // Refresh data
      setBatchDetails(prev => {
        const updated = { ...prev };
        delete updated[primarySku];
        return updated;
      });
      setTimeout(() => loadBatchDetails(primarySku, null, true), 100);
      loadData();
    } catch (err) {
      console.error('Failed to adjust warehouse stock:', err);
      setError(err.response?.data?.error || 'Failed to adjust warehouse stock');
    } finally {
      setEditWarehouseLoading(false);
    }
  };

  // Submit batch adjustment
  const handleSubmitAdjustment = async () => {
    if (!adjustmentForm.batch || !adjustmentForm.adjustment_quantity || !adjustmentForm.reason) {
      setError('Please fill in all required fields');
      return;
    }

    setAdjustmentLoading(true);
    try {
      await createBatchAdjustment({
        source_batch_id: adjustmentForm.batch.id,
        adjustment_quantity: parseFloat(adjustmentForm.adjustment_quantity),
        adjustment_type: adjustmentForm.adjustment_type,
        reason: adjustmentForm.reason
      });

      setSuccess(`Adjustment of ${adjustmentForm.adjustment_quantity} applied to batch ${adjustmentForm.batch.batch_number}`);
      setAdjustmentDialogOpen(false);

      // Refresh batch details for this SKU
      const key = adjustmentForm.primarySku;
      setBatchDetails(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      // Reload batch details
      setTimeout(() => loadBatchDetails(adjustmentForm.primarySku, null, true), 100);

      // Reload stock data to reflect new totals
      loadData();
    } catch (err) {
      console.error('Failed to create adjustment:', err);
      setError(err.response?.data?.error || 'Failed to create adjustment');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Get unmapped SKUs warning from configWarnings
  const unmappedSkusWarning = useMemo(() => {
    return configWarnings.find(w => w.type === 'unmapped_skus');
  }, [configWarnings]);

  // Other configuration warnings (excluding unmapped SKUs)
  const otherWarnings = useMemo(() => {
    return configWarnings.filter(w => w.type !== 'unmapped_skus');
  }, [configWarnings]);

  // Calculate totals from stock items (filtered by active tab)
  const totals = useMemo(() => {
    // All Brands: consolidated company-wide totals from the daily snapshot
    // (stockItems is intentionally empty at All Brands — the per-SKU table is
    // brand-scoped). Low-stock needs per-SKU velocity we don't carry here → 0.
    if (isAllBrands) {
      const t = stockSummary.totals || {};
      return {
        skus: t.total_skus || 0,
        units: t.total_units || 0,
        value: t.total_value || 0,
        lowStock: 0
      };
    }
    // Filter items by active tab first
    const itemsForTotals = stockItems.filter(item => {
      if (activeTab === 0) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        return level === 'L1' || isPrepacked;
      } else if (activeTab === 1) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        return level === 'L0' && !isPrepacked;
      }
      return true; // All tab
    });

    return itemsForTotals.reduce((acc, item) => {
      // Low stock count: Check if ANY warehouse has stock issues
      // FBA warehouses use 30-day threshold, internal warehouses use 90-day threshold
      const warehouses = item.warehouses || [];
      const hasOutOfStock = warehouses.some(wh => {
        const whSold = wh.sold_90d || 0;
        return whSold > 0 && wh.quantity <= 0;
      });
      const hasLowStock = warehouses.some(wh => {
        const whSold = wh.sold_90d || 0;
        const isFBA = (wh.warehouse_code || '').endsWith('-FBA');
        // FBA: low stock if qty < 30 days of sales (sold_90d / 3)
        // Internal: low stock if qty < 90 days of sales
        const threshold = isFBA ? whSold / 3 : whSold;
        return whSold > 0 && wh.quantity > 0 && wh.quantity < threshold;
      });
      return {
        skus: acc.skus + 1,
        units: acc.units + (item.total_quantity || 0),
        value: acc.value + (item.total_value || 0),
        lowStock: acc.lowStock + (hasOutOfStock || hasLowStock ? 1 : 0)
      };
    }, { skus: 0, units: 0, value: 0, lowStock: 0 });
  }, [stockItems, activeTab, isAllBrands, stockSummary]);

  // Get all unique warehouses from stock items
  const allWarehouses = useMemo(() => {
    const whs = new Set();
    stockItems.forEach(item => {
      (item.warehouses || []).forEach(wh => whs.add(wh.warehouse_code));
    });
    return Array.from(whs).sort();
  }, [stockItems]);

  // Calculate warehouse summary based on filtered stock (respects active tab)
  const filteredWarehouseSummary = useMemo(() => {
    // All Brands: consolidated per-warehouse totals from the daily snapshot.
    if (isAllBrands) {
      const mapTiles = (arr, isExternal) => (arr || [])
        .map(w => ({
          warehouse_code: w.warehouse_code,
          total_quantity: w.total_quantity || 0,
          is_external: isExternal
        }))
        .sort((a, b) => b.total_quantity - a.total_quantity);
      return {
        internal: mapTiles(stockSummary.internal, false),
        external: mapTiles(stockSummary.external, true)
      };
    }

    const warehouseMap = {};

    // Use the tab-filtered items to calculate warehouse totals
    const itemsForSummary = stockItems.filter(item => {
      if (activeTab === 0) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        return level === 'L1' || isPrepacked;
      } else if (activeTab === 1) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        return level === 'L0' && !isPrepacked;
      }
      return true; // All tab
    });

    itemsForSummary.forEach(item => {
      (item.warehouses || []).forEach(wh => {
        const code = wh.warehouse_code;
        if (!warehouseMap[code]) {
          warehouseMap[code] = {
            warehouse_code: code,
            total_quantity: 0,
            is_external: wh.is_external || false
          };
        }
        warehouseMap[code].total_quantity += wh.quantity || 0;
      });
    });

    const all = Object.values(warehouseMap);
    return {
      internal: all.filter(wh => !wh.is_external).sort((a, b) => b.total_quantity - a.total_quantity),
      external: all.filter(wh => wh.is_external).sort((a, b) => b.total_quantity - a.total_quantity)
    };
  }, [stockItems, activeTab, isAllBrands, stockSummary]);

  // Expiry status per SKU (scoped to the selected warehouse): expired / expiring
  // soon / ok / no date, earliest expiry date, and units blocked from sellable.
  // null while that SKU's batches are still loading.
  const expiryBySku = useMemo(() => {
    const map = {};
    for (const item of stockItems) {
      const sku = item.primary_sku || item.parent_sku;
      if (!sku) continue;
      const batches = batchDetails[sku];
      map[sku] = batches === undefined ? null : getExpiryInfo({
        item,
        batches,
        warehouseExpiry,
        warehouseCode: warehouseFilter !== 'all' ? warehouseFilter : null,
      });
    }
    return map;
  }, [stockItems, batchDetails, warehouseExpiry, warehouseFilter]);

  // Filter and search
  const filteredStock = useMemo(() => {
    return stockItems.filter(item => {
      // Tab filter
      // Tab 0: Products - L1 OR (L2 and prepacked)
      // Tab 1: Parts - L2 or higher, NOT prepacked
      // Tab 2: All
      if (activeTab === 0) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        // Products: L1, or L2+ that are prepacked
        if (level === 'L1' || isPrepacked) {
          // This is a product, keep it
        } else {
          return false; // Not a product
        }
      } else if (activeTab === 1) {
        const level = item.product_level || 'L1';
        const isPrepacked = item.is_prepacked || false;
        // Parts: L0 raw components/packaging only. Sellable L2 bundles are virtual
        // combos (NOT physical stock, NOT L0), so they don't belong here — they show
        // under "All". (Rishabh 2026-09-01; prepacked bundles are physical → Products.)
        if (level === 'L0' && !isPrepacked) {
          // This is a part, keep it
        } else {
          return false; // Not a part
        }
      }
      // Tab 2 (All) shows everything

      // Search filter - use primary_sku (from API) or fallback to parent_sku
      const primarySku = item.primary_sku || item.parent_sku || '';
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!primarySku.toLowerCase().includes(search) &&
            !item.product_name?.toLowerCase().includes(search) &&
            !(item.child_skus || []).some(s => s.toLowerCase().includes(search)) &&
            !(item.gtins || []).some(g => g.toLowerCase().includes(search)) &&
            !(item.asins || []).some(a => a.toLowerCase().includes(search))) {
          return false;
        }
      }

      // Warehouse filter
      if (warehouseFilter !== 'all') {
        const hasWarehouse = (item.warehouses || []).some(wh => wh.warehouse_code === warehouseFilter);
        if (!hasWarehouse) return false;
      }

      // Stock level filter — keep semantics in lock-step with the SKU-level
      // status chip (getStockStatusLabel) so the dropdown filter and the chip
      // never disagree. Pre-fix the "Out" filter matched any-warehouse-out,
      // which conflicted with the chip's new "total qty = 0" definition.
      const warehouses = item.warehouses || [];
      const totalQty = Number(item.total_quantity || item.quantity || 0);
      if (stockFilter === 'low') {
        // Low stock: any warehouse with sales but qty <= threshold (FBA: /3,
        // internal: 1x). Per-warehouse stockouts (qty=0 with sales) also fold
        // into low so a SKU with 10 warehouses where 1 is empty appears here
        // instead of in Out-of-Stock.
        const hasLowStock = warehouses.some(wh => {
          const whSold = Number(wh.sold_90d) || 0;
          if (whSold <= 0) return false;
          const qtyHere = Number(wh.quantity) || 0;
          if (qtyHere <= 0) return true;
          const isFBA = (wh.warehouse_code || '').endsWith('-FBA');
          const threshold = isFBA ? whSold / 3 : whSold;
          return qtyHere < threshold;
        });
        if (!hasLowStock) return false;
      } else if (stockFilter === 'out') {
        // Out of stock: SKU TOTAL qty = 0 across every warehouse.
        if (totalQty > 0) return false;
      } else if (stockFilter === 'expiring') {
        // Expiring: has expiring-soon (or expired) stock. PRIMARY signal is the
        // item's own expiring_quantity/expired_quantity, which the API now returns
        // synchronously (movement-based, 90-day window — same source as the
        // "Expiring Soon" count). This makes a deep-link ?filter=expiring render
        // immediately, independent of the async per-batch load. The batchDetails
        // check remains as a fallback so anything the batch view flags but the
        // item-level rollup misses still surfaces once batches load.
        const itemFlag = (item.expiring_quantity || 0) > 0 || (item.expired_quantity || 0) > 0;
        const batches = batchDetails[primarySku] || [];
        const batchFlag = batches.some(b => (b.current_quantity || 0) > 0 && (b.is_expired || b.is_expiring));
        if (!itemFlag && !batchFlag) return false;
      } else if (stockFilter === 'expired' || stockFilter === 'expiring_soon' || stockFilter === 'no_expiry') {
        const info = expiryBySku[primarySku];
        if (!info) return false;
        const wanted = { expired: 'expired', expiring_soon: 'soon', no_expiry: 'none' }[stockFilter];
        if (info.status !== wanted) return false;
        if (stockFilter === 'no_expiry' && totalQty <= 0) return false;
      }

      return true;
    });
    // batchDetails IS a dependency: the `expiring` filter reads it, and it loads
    // ~3-5s AFTER stockItems via the bulk-batches endpoint. Without it here a
    // deep-link like ?filter=expiring computes an empty list and never recomputes
    // when the flags land (the bug). Safe against loops: the bulk-load effect keys
    // on [stockItems], not batchDetails, so a recompute here never re-triggers a
    // load. Re-filtering ~150 items on each batch update is negligible.
  }, [stockItems, searchTerm, warehouseFilter, stockFilter, activeTab, batchDetails, expiryBySku]);

  // When a specific warehouse is selected, sort by that warehouse's qty/value
  // instead of cross-warehouse totals — keeps ranking consistent with what's shown.
  const sortedStock = useMemo(() => {
    const sorted = [...filteredStock];
    const getWh = (item) => warehouseFilter === 'all'
      ? null
      : (item.warehouses || []).find(w => w.warehouse_code === warehouseFilter);

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'primary_sku':
        case 'parent_sku':
          aVal = (a.primary_sku || a.parent_sku || '').toLowerCase();
          bVal = (b.primary_sku || b.parent_sku || '').toLowerCase();
          break;
        case 'product_name':
          aVal = (a.product_name || '').toLowerCase();
          bVal = (b.product_name || '').toLowerCase();
          break;
        case 'total_quantity': {
          const awh = getWh(a), bwh = getWh(b);
          aVal = awh ? (awh.quantity || 0) : (a.total_quantity || 0);
          bVal = bwh ? (bwh.quantity || 0) : (b.total_quantity || 0);
          break;
        }
        case 'total_value': {
          const awh = getWh(a), bwh = getWh(b);
          aVal = awh ? (awh.value || 0) : (a.total_value || 0);
          bVal = bwh ? (bwh.value || 0) : (b.total_value || 0);
          break;
        }
        case 'expiring_quantity': {
          // #2: sort by at-risk stock — units expiring within 90 days plus already-expired,
          // so the most time-critical SKUs surface at the top (desc). Pairs with the
          // existing "Expiring / Expired" filter + the 90-day badges.
          aVal = (a.expiring_quantity || 0) + (a.expired_quantity || 0);
          bVal = (b.expiring_quantity || 0) + (b.expired_quantity || 0);
          break;
        }
        case 'expiry_date': {
          // Soonest expiry first; SKUs with no known date always sort last.
          const ad = expiryBySku[a.primary_sku || a.parent_sku]?.daysLeft;
          const bd = expiryBySku[b.primary_sku || b.parent_sku]?.daysLeft;
          const aMissing = ad === null || ad === undefined;
          const bMissing = bd === null || bd === undefined;
          if (aMissing || bMissing) return aMissing === bMissing ? 0 : (aMissing ? 1 : -1);
          aVal = ad;
          bVal = bd;
          break;
        }
        case 'sellable_quantity': {
          const sell = (item) => {
            const wh = getWh(item);
            const onHand = wh ? (wh.quantity || 0) : (item.total_quantity || 0);
            return Math.max(0, onHand - (expiryBySku[item.primary_sku || item.parent_sku]?.blockedQty || 0));
          };
          aVal = sell(a);
          bVal = sell(b);
          break;
        }
        default:
          return 0;
      }

      let comparison = 0;
      if (typeof aVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = aVal - bVal;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredStock, sortField, sortDirection, warehouseFilter, expiryBySku]);

  // Counters above the table: SKUs + units by expiry status, for the current tab
  // and warehouse (ignores the Stock Status dropdown so they always show the
  // full picture; clicking one sets that filter).
  const expiryCounts = useMemo(() => {
    const counts = { expired: { skus: 0, units: 0 }, soon: { skus: 0, units: 0 }, none: { skus: 0, units: 0 } };
    for (const item of stockItems) {
      const level = item.product_level || 'L1';
      const isPrepacked = item.is_prepacked || false;
      if (activeTab === 0 && !(level === 'L1' || isPrepacked)) continue;
      if (activeTab === 1 && !(level === 'L0' && !isPrepacked)) continue;
      const wh = warehouseFilter !== 'all' ? (item.warehouses || []).find(w => w.warehouse_code === warehouseFilter) : null;
      if (warehouseFilter !== 'all' && !wh) continue;
      const onHand = Math.max(0, Number(wh ? wh.quantity : item.total_quantity) || 0);
      if (onHand <= 0) continue;
      const info = expiryBySku[item.primary_sku || item.parent_sku];
      if (!info || !counts[info.status]) continue;
      counts[info.status].skus += 1;
      counts[info.status].units += info.status === 'expired' ? info.blockedQty || onHand : onHand;
    }
    return counts;
  }, [stockItems, expiryBySku, activeTab, warehouseFilter]);

  const EXPIRY_STATUS_STYLE = {
    expired: { label: 'Expired', color: 'error' },
    soon: { label: 'Expiring soon', color: 'warning' },
    ok: { label: 'OK', color: 'success' },
    none: { label: 'No date', color: 'default' },
  };

  // Expiry cell: status chip + the earliest expiry date and how far away it is.
  const renderExpiry = (info) => {
    if (!info) {
      return <Typography variant="caption" color="text.secondary">…</Typography>;
    }
    const style = EXPIRY_STATUS_STYLE[info.status];
    const tooltip = info.status === 'none'
      ? 'No expiry date recorded for the stock on hand (receipts, batches or warehouse lots).'
      : `Earliest expiry of stock on hand. Expiring soon = within ${Math.round(EXPIRING_SOON_DAYS / 30.44)} months.`;
    return (
      <Tooltip title={tooltip}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
          <Chip label={style.label} size="small" color={style.color} variant={info.status === 'none' ? 'outlined' : 'filled'} />
          {info.earliestDate && (
            <Typography variant="caption" color={info.status === 'expired' ? 'error.main' : info.status === 'soon' ? 'warning.dark' : 'text.secondary'}>
              {formatDate(info.earliestDate)} · {describeDaysLeft(info.daysLeft)}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  };

  // Pagination
  const paginatedStock = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedStock.slice(start, start + rowsPerPage);
  }, [sortedStock, page, rowsPerPage]);

  // Handle column sort
  const handleSort = (field) => {
    let newDirection;
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      newDirection = 'asc';
      setSortField(field);
      setSortDirection(newDirection);
    }
    setPage(0);
    updateUrlParams({ sortBy: field, sortDir: newDirection, page: 0 });
  };

  // Render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ?
      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />;
  };

  const handleExpandRow = (item) => {
    const sku = item.primary_sku || item.parent_sku;
    const isExpanding = !expandedRows[sku];
    setExpandedRows(prev => ({
      ...prev,
      [sku]: isExpanding
    }));

    // Load batch + inbound details when expanding
    if (isExpanding) {
      loadBatchDetails(sku);
      loadIncomingDetails(sku);
    }
  };

  const handleReceiveSubmit = async () => {
    // Stock Levels is brand-scoped — a brand must be selected so the received
    // stock is attributed to the right brand (that is the value the engine filters on).
    if (!selectedBrand?.id) {
      setError('Select a brand before receiving stock');
      return;
    }
    if (receiving) return; // in-flight guard against double-submit
    try {
      setReceiving(true);
      setError(null);
      await receiveBatch({ ...receiveForm, brand_id: selectedBrand.id });
      setSuccess('Inventory received successfully');
      setReceiveDialogOpen(false);
      setReceiveForm({
        sku: '',
        warehouse_id: '',
        batch_number: '',
        quantity: '',
        unit_cost: '',
        expiry_date: ''
      });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to receive inventory');
    } finally {
      setReceiving(false);
    }
  };

  // Stock status logic — SKU-level chip aggregates per-warehouse signals.
  //
  // Out of Stock: the SKU TOTAL qty is 0 (genuinely nothing left anywhere).
  //   Pre-fix this flipped to 'Out of Stock' the moment ANY warehouse with
  //   sales hit qty=0 — even if other warehouses had thousands of units. That
  //   was misleading at the SKU-summary level (Rishabh: SS0000012010 had
  //   15,358 units across 11 warehouses but the chip said Out of Stock
  //   because one warehouse had qty=0 with sales). Per-warehouse stockouts
  //   now fold into the "Low Stock" (warning) bucket instead, and the
  //   warehouse-breakdown table still surfaces the offending warehouse.
  //
  // Low Stock: any warehouse with sales > 0 has either qty=0 OR qty below
  //   its threshold (FBA: sold_90d/3, internal: sold_90d).
  const isWarehouseLow = (wh) => {
    const whSold = Number(wh.sold_90d) || 0;
    if (whSold <= 0) return false;
    const qtyHere = Number(wh.quantity) || 0;
    if (qtyHere <= 0) return true; // sells from this WH but ran out → low for SKU
    const isFBA = (wh.warehouse_code || '').endsWith('-FBA');
    const threshold = isFBA ? whSold / 3 : whSold;
    return qtyHere < threshold;
  };

  const getStockStatusColor = (qty, sold90d = 0, warehouses = []) => {
    const totalQty = Number(qty) || 0;
    if (warehouses && warehouses.length > 0) {
      if (totalQty <= 0) return 'error'; // truly out everywhere
      if (warehouses.some(isWarehouseLow)) return 'warning';
      const hasSales = warehouses.some(wh => (wh.sold_90d || 0) > 0);
      if (hasSales) return 'success';
    } else if (totalQty <= 0 && (sold90d || 0) > 0) {
      return 'error';
    }
    return 'default';
  };

  const getStockStatusLabel = (qty, sold90d = 0, warehouses = []) => {
    const totalQty = Number(qty) || 0;
    if (warehouses && warehouses.length > 0) {
      if (totalQty <= 0) return 'Out of Stock';
      const hasLowStock = warehouses.some(isWarehouseLow);
      if (hasLowStock) return 'Low Stock';

      // Check if any warehouse has sales
      const hasSales = warehouses.some(wh => (wh.sold_90d || 0) > 0);
      if (hasSales) return 'In Stock';
    }
    // No warehouses or no sales
    if (qty <= 0) return 'No Stock';
    return 'In Stock';
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setImportFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    if (!selectedBrand?.id) {
      setError('Please select a brand before importing');
      return;
    }

    try {
      setImporting(true);
      setError(null);

      const result = await bulkImportBatches(importFile, selectedBrand.id);
      setSuccess(`Import completed: ${result.success_count || 0} succeeded, ${result.error_count || 0} failed`);

      setImportDialogOpen(false);
      setImportFile(null);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to import inventory'));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate('inventory');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // The KPI totals (SKUs/Units/Value) are scoped to the active Products/Parts/
  // All tab (totals useMemo keys on activeTab), so the qualifier must track the
  // tab — a static "(products only)" would be wrong on the Parts/All tabs.
  const scopeLabel = isAllBrands ? 'all brands' : (activeTab === 0 ? 'products only' : activeTab === 1 ? 'parts only' : 'all items');

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <InventoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Stock Levels
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inventory as of {new Date(asOfDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                {asOfDate !== new Date().toISOString().split('T')[0] && (
                  <Chip
                    label="Historical"
                    size="small"
                    color="info"
                    sx={{ ml: 1 }}
                  />
                )}
                {isAllBrands && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    · Company-wide totals across all brands
                    {stockSummary.totals?.snapshot_date
                      ? ` (from the daily stock snapshot of ${new Date(stockSummary.totals.snapshot_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})`
                      : ' (from the daily stock snapshot)'}
                  </Typography>
                )}
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Brand</InputLabel>
              <Select
                value={isAllBrands ? 'all' : (selectedBrand?.id || '')}
                onChange={handleBrandChange}
                label="Filter by Brand"
              >
                <MenuItem value="all"><strong>All Brands</strong></MenuItem>
                {excludeCompanyLevel(brands).map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="As of Date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
              inputProps={{ min: '2000-01-01', max: new Date().toISOString().split('T')[0] }}
            />
            {asOfDate !== new Date().toISOString().split('T')[0] && (
              <Button
                size="small"
                variant="text"
                onClick={() => setAsOfDate(new Date().toISOString().split('T')[0])}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Today
              </Button>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={selectedBrand ? "Export inventory summary to CSV" : "Select a brand to export"}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (!selectedBrand?.id) { setError('Please select a brand to export'); return; }
                      setExportMode('summary');
                      setError(null); setExportDialogOpen(true);
                    }}
                    disabled={!selectedBrand}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={selectedBrand ? "Export with movement breakdown (Opening Balance, Receipts, Transfers, etc.)" : "Select a brand to export"}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (!selectedBrand?.id) { setError('Please select a brand to export'); return; }
                      setExportMode('detailed');
                      setError(null); setExportDialogOpen(true);
                    }}
                    disabled={!selectedBrand}
                    sx={{ ml: 1 }}
                  >
                    Detailed Export
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => { setError(null); setImportDialogOpen(true); }}
              >
                Import
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setReceiveDialogOpen(true); }}
              >
                Receive Stock
              </Button>
              <Tooltip title="Refresh data (bypass cache)">
                <IconButton onClick={() => loadData(true)} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Unmapped SKUs Warning - Links to Products & COGS page */}
      {unmappedSkusWarning && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          icon={<ErrorIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/inventory/cogs?tab=4')}
            >
              Fix Now
            </Button>
          }
        >
          <AlertTitle>
            {(unmappedSkusWarning.skus?.length || 0) === 1
              ? '1 Unmapped SKU Found'
              : `${unmappedSkusWarning.skus?.length || 0} Unmapped SKUs Found`}
          </AlertTitle>
          <Typography variant="body2">
            {unmappedSkusWarning.message} Go to Products & COGS → SKU Mappings to resolve.
          </Typography>
        </Alert>
      )}

      {/* Other Configuration Warnings */}
      {otherWarnings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {otherWarnings.map((warning, idx) => (
            <Alert
              key={idx}
              severity={warning.severity === 'error' ? 'error' : 'warning'}
              sx={{ mb: 1 }}
              icon={<WarningIcon />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/inventory/config')}
                >
                  Configure
                </Button>
              }
            >
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {warning.message}
                </Typography>
                {warning.action && (
                  <Typography variant="caption" color="text.secondary">
                    {warning.action}
                  </Typography>
                )}
              </Box>
            </Alert>
          ))}
        </Box>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Total SKUs
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>({scopeLabel})</Typography>
              </Typography>
              <Typography variant="h4" fontWeight="bold">{formatNumber(totals.skus)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Total Units
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>({scopeLabel})</Typography>
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(totals.units)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Total Value
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>({scopeLabel})</Typography>
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(totals.value)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Low Stock Items</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {formatNumber(totals.lowStock)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Warehouse Summary - filtered by active tab */}
      {(filteredWarehouseSummary.internal.length > 0 || filteredWarehouseSummary.external.length > 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Stock by Warehouse
              {!isAllBrands && activeTab === 0 && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>(Products only)</Typography>}
              {!isAllBrands && activeTab === 1 && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>(Parts only)</Typography>}
              {isAllBrands && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>(all physical stock incl. components — the Total Units above is finished goods only)</Typography>}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {filteredWarehouseSummary.internal.map(wh => (
                <Chip
                  key={wh.warehouse_code}
                  icon={<StoreIcon />}
                  label={`${wh.warehouse_code}: ${formatNumber(wh.total_quantity)} units`}
                  color="default"
                  variant="outlined"
                  onClick={isAllBrands ? undefined : () => { setWarehouseFilter(wh.warehouse_code); setPage(0); updateUrlParams({ warehouse: wh.warehouse_code, page: 0 }); }}
                />
              ))}
              {filteredWarehouseSummary.external.map(wh => (
                <Chip
                  key={wh.warehouse_code}
                  icon={<ShippingIcon />}
                  label={`${wh.warehouse_code}: ${formatNumber(wh.total_quantity)} units`}
                  color="primary"
                  variant="outlined"
                  onClick={isAllBrands ? undefined : () => { setWarehouseFilter(wh.warehouse_code); setPage(0); updateUrlParams({ warehouse: wh.warehouse_code, page: 0 }); }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Always-visible filter toolbar: Search + Warehouse + Stock Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by SKU, product name, GTIN, or ASIN..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Warehouse</InputLabel>
                <Select
                  value={warehouseFilter}
                  label="Warehouse"
                  onChange={(e) => { setWarehouseFilter(e.target.value); setPage(0); updateUrlParams({ warehouse: e.target.value, page: 0 }); }}
                >
                  <MenuItem value="all">All Warehouses</MenuItem>
                  {warehouses.map(wh => (
                    <MenuItem key={wh.code} value={wh.code}>{wh.code} - {wh.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Stock Status</InputLabel>
                <Select
                  value={stockFilter}
                  label="Stock Status"
                  onChange={(e) => { setStockFilter(e.target.value); setPage(0); updateUrlParams({ filter: e.target.value, page: 0 }); }}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  <MenuItem value="low">Low Stock</MenuItem>
                  <MenuItem value="out">Out of Stock</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="expiring_soon">Expiring soon (9 months)</MenuItem>
                  <MenuItem value="no_expiry">No expiry date</MenuItem>
                  <MenuItem value="expiring">Expiring / Expired (90 days, batches)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  setWarehouseFilter('all');
                  setStockFilter('all');
                  setPage(0);
                  updateUrlParams({ search: '', warehouse: 'all', filter: 'all', page: 0 });
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs for Products / Parts / All */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setPage(0); // Reset pagination when switching tabs
            updateUrlParams({ tab: newValue, page: 0 });
          }}
          aria-label="stock levels tabs"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Products
                <Chip
                  label={stockItems.filter(item => {
                    const level = item.product_level || 'L1';
                    const isPrepacked = item.is_prepacked || false;
                    return level === 'L1' || isPrepacked;
                  }).length}
                  size="small"
                  color={activeTab === 0 ? 'primary' : 'default'}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Parts
                <Chip
                  label={stockItems.filter(item => {
                    const level = item.product_level || 'L1';
                    const isPrepacked = item.is_prepacked || false;
                    return level === 'L0' && !isPrepacked;
                  }).length}
                  size="small"
                  color={activeTab === 1 ? 'primary' : 'default'}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                All
                <Chip
                  label={stockItems.length}
                  size="small"
                  color={activeTab === 2 ? 'primary' : 'default'}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Audit C2: define what "Parts" contains — the bare tab name read as
          "finished-goods parts" and its valuation was repeatedly mistaken for
          a product-stock figure. */}
      {activeTab === 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
          Parts = components only: raw materials, packaging and non-prepacked kit contents (L0 / L2+).
          Finished sellable goods are on the Products tab; their value is not included here.
        </Typography>
      )}

      {/* Expiry at a glance: click a counter to filter the table */}
      {!isAllBrands && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Expiry:</Typography>
          {[
            { key: 'expired', filter: 'expired', label: 'Expired', color: 'error' },
            { key: 'soon', filter: 'expiring_soon', label: 'Expiring soon', color: 'warning' },
            { key: 'none', filter: 'no_expiry', label: 'No date', color: 'default' },
          ].map(c => (
            <Chip
              key={c.key}
              color={c.color}
              variant={stockFilter === c.filter ? 'filled' : 'outlined'}
              label={`${c.label}: ${formatNumber(expiryCounts[c.key].skus)} SKUs · ${formatNumber(expiryCounts[c.key].units)} units`}
              onClick={() => {
                const next = stockFilter === c.filter ? 'all' : c.filter;
                setStockFilter(next); setPage(0); updateUrlParams({ filter: next, page: 0 });
              }}
            />
          ))}
        </Box>
      )}

      {/* Stock Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('primary_sku')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Primary SKU
                    {renderSortIcon('primary_sku')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('product_name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Product Name
                    {renderSortIcon('product_name')}
                  </Box>
                </TableCell>
                <TableCell>GTIN</TableCell>
                <TableCell
                  align="right"
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('total_quantity')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    On hand
                    {renderSortIcon('total_quantity')}
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('sellable_quantity')}
                >
                  <Tooltip title="On hand minus units that are expired or on hold (reported by the warehouse, or past their batch expiry date). Those units stay in On hand and in stock value until a disposal or return is booked.">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Sellable
                      {renderSortIcon('sellable_quantity')}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('total_value')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Value
                    {renderSortIcon('total_value')}
                  </Box>
                </TableCell>
                <TableCell>Warehouses</TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('expiry_date')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Expiry
                    {renderSortIcon('expiry_date')}
                  </Box>
                </TableCell>
                <TableCell>Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStock.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 4 }}>
                      <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="textSecondary">
                        {isAllBrands
                          ? 'All Brands shows consolidated totals in the summary above. Select a specific brand to see per-SKU stock.'
                          : 'No stock items found matching your criteria'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedStock.map((item) => {
                  const primarySku = item.primary_sku || item.parent_sku;
                  const primaryGtin = (item.gtins && item.gtins.length > 0) ? item.gtins[0] : '';
                  // When a specific warehouse is selected, show per-warehouse stock for
                  // this SKU instead of cross-warehouse totals.
                  const selectedWh = warehouseFilter !== 'all'
                    ? (item.warehouses || []).find(w => w.warehouse_code === warehouseFilter)
                    : null;
                  const displayQty = selectedWh ? (selectedWh.quantity || 0) : (item.total_quantity || 0);
                  const displayValue = selectedWh ? (selectedWh.value || 0) : (item.total_value || 0);
                  const displaySold90d = selectedWh ? (selectedWh.sold_90d || 0) : (item.sold_90d || 0);
                  const displayWarehouses = selectedWh ? [selectedWh] : (item.warehouses || []);
                  const expiry = expiryBySku[primarySku];
                  const blockedQty = expiry?.blockedQty || 0;
                  const sellableQty = Math.max(0, (Number(displayQty) || 0) - blockedQty);
                  return (
                  <React.Fragment key={primarySku}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(item)}
                        >
                          {expandedRows[primarySku] ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          fontWeight="medium"
                          onClick={() => {
                            // Remember the current Stock Levels filters so Back restores them.
                            const back = searchParams.toString();
                            navigate(`/inventory/cogs?product=${encodeURIComponent(primarySku)}&from=stock${back ? `&back=${encodeURIComponent(back)}` : ''}`);
                          }}
                          title="Open in Products & COGS"
                          sx={{ cursor: 'pointer', color: 'primary.main', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {primarySku}
                        </Typography>
                        {item.child_skus && item.child_skus.filter(s => s !== primarySku).length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            +{item.child_skus.filter(s => s !== primarySku).length} alias(es)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.product_name || ''}>
                            {item.product_name || '—'}
                          </Typography>
                          {item.is_bundle && item.product_level === 'L2' && (
                            <Chip label="Bundle" size="small" color="info" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {primaryGtin || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={selectedWh ? `Quantity at ${warehouseFilter}` : 'Total across all warehouses'}>
                          <Typography fontWeight="medium">
                            {formatNumber(displayQty)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {blockedQty > 0 ? (
                          <Tooltip title={(expiry.blocked || []).map(b => `${formatNumber(b.qty)} ${b.sources.map(s => HOLD_SOURCE_LABEL[s] || s).join(' / ')} · ${b.warehouse_code}`).join('\n')}>
                            <Box>
                              <Typography fontWeight="medium">{formatNumber(sellableQty)}</Typography>
                              <Typography variant="caption" color="error.main" sx={{ whiteSpace: 'nowrap' }}>
                                −{formatNumber(blockedQty)} expired/on hold
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography fontWeight="medium" color={expiry ? 'text.primary' : 'text.secondary'}>
                            {expiry ? formatNumber(sellableQty) : '…'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {/* Show "no cost" indicator instead of $0.00 when the
                            SKU has inventory but no product_cogs entry —
                            otherwise operators can't distinguish "we have
                            stock with zero cost basis" (rare) from "we have
                            stock but COGS is missing" (common data gap). */}
                        {(Number(displayValue) || 0) === 0 && (Number(displayQty) || 0) > 0 ? (
                          <Tooltip title="No COGS configured — set cost in Products & COGS to value this inventory">
                            <Typography variant="body2" sx={{ color: 'warning.dark', fontStyle: 'italic', cursor: 'help' }}
                              onClick={(e) => { e.stopPropagation(); navigate('/inventory/cogs'); }}>
                              no cost set
                            </Typography>
                          </Tooltip>
                        ) : (
                          formatCurrency(displayValue)
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {displayWarehouses.slice(0, 3).map(wh => (
                            <Chip
                              key={wh.warehouse_code}
                              label={selectedWh ? `${wh.warehouse_code}: ${formatNumber(wh.quantity || 0)}` : wh.warehouse_code}
                              size="small"
                              variant={selectedWh ? 'filled' : 'outlined'}
                              color={wh.is_external ? 'primary' : 'default'}
                            />
                          ))}
                          {displayWarehouses.length > 3 && (
                            <Chip label={`+${displayWarehouses.length - 3}`} size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {renderExpiry(expiry)}
                      </TableCell>
                      <TableCell>
                        {/* Stock status only; expiry has its own column. */}
                        <Chip
                          label={getStockStatusLabel(displayQty, displaySold90d, displayWarehouses)}
                          size="small"
                          color={getStockStatusColor(displayQty, displaySold90d, displayWarehouses)}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row - Warehouse Breakdown */}
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expandedRows[primarySku]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 4, backgroundColor: 'grey.50' }}>
                            {/* Warehouse Breakdown */}
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                              Stock by Warehouse
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                  <TableCell>Warehouse</TableCell>
                                  <TableCell align="right">Quantity</TableCell>
                                  <TableCell align="right">
                                    <Tooltip arrow placement="top" title="The 3PL / warehouse's OWN reported on-hand, pulled daily from its portal (IPS, SYN, OMN, ShipHero), shown next to our calculated Quantity so drift is visible. Display-only — it never changes the calculation. The small Δ is calculated − reported. Shows '—' where that warehouse has no feed.">
                                      <span style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>3PL Reported</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip arrow placement="top" title="Variance at the warehouse's last physical count: reported count − our calculated stock as of that count's date (from the daily snapshot). Shows '—' where no snapshot exists for that date.">
                                      <span style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>Count Var</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">90d Shipped</TableCell>
                                  <TableCell align="right">Inbound</TableCell>
                                  <TableCell align="right">Avg Cost</TableCell>
                                  <TableCell align="right">Value</TableCell>
                                  <TableCell align="center">Batches</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell align="center" sx={{ width: 60 }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(item.warehouses || [])
                                  .sort((a, b) => b.quantity - a.quantity)
                                  .map((wh, idx) => {
                                    // Warehouse-level stock status: Only flag if there are sales in last 90 days
                                    const warehouseSold90d = wh.sold_90d || 0;
                                    // Low stock if sold in 90d and qty < sales, OR out of stock with sales
                                    const isWarehouseLowStock = warehouseSold90d > 0 && wh.quantity < warehouseSold90d;
                                    const isWarehouseOutOfStock = warehouseSold90d > 0 && wh.quantity <= 0;

                                    // Expiry at this warehouse: same rules as the SKU row's Expiry column
                                    // (warehouse-reported expired / on hold + SCM batch expiry dates).
                                    const whExpiry = getExpiryInfo({
                                      item,
                                      batches: batchDetails[primarySku] || [],
                                      warehouseExpiry,
                                      warehouseCode: wh.warehouse_code,
                                    });
                                    const warehouseBatches = (batchDetails[primarySku] || []).filter(b => b.warehouse_code === wh.warehouse_code);
                                    const expiredUnits = whExpiry.blockedQty;
                                    const hasExpiredBatches = whExpiry.status === 'expired';
                                    const hasExpiringBatches = whExpiry.status === 'soon';

                                    return (
                                    <TableRow
                                      key={wh.warehouse_code || idx}
                                      hover
                                      sx={{
                                        backgroundColor: isWarehouseOutOfStock
                                          ? 'error.lighter'
                                          : hasExpiredBatches
                                            ? '#ffebee' // light red for expired
                                            : hasExpiringBatches
                                              ? '#fff3e0' // light orange for expiring soon
                                              : isWarehouseLowStock
                                                ? 'warning.lighter'
                                                : 'inherit',
                                        '&:hover': {
                                          backgroundColor: isWarehouseOutOfStock
                                            ? 'error.light'
                                            : hasExpiredBatches
                                              ? '#ffcdd2'
                                              : hasExpiringBatches
                                                ? '#ffe0b2'
                                                : isWarehouseLowStock
                                                  ? 'warning.light'
                                                  : undefined
                                        }
                                      }}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Chip
                                            label={wh.warehouse_code}
                                            size="small"
                                            color={isWarehouseOutOfStock ? 'error' : isWarehouseLowStock ? 'warning' : wh.is_external ? 'primary' : 'default'}
                                            variant={isWarehouseLowStock || isWarehouseOutOfStock ? 'filled' : 'outlined'}
                                          />
                                          {whExpiry.status !== 'none' && (
                                            <Tooltip title={[
                                              whExpiry.earliestDate ? `Earliest expiry ${formatDate(whExpiry.earliestDate)} (${describeDaysLeft(whExpiry.daysLeft)})` : null,
                                              ...whExpiry.blocked.map(b => `${formatNumber(b.qty)} ${b.sources.map(s => HOLD_SOURCE_LABEL[s] || s).join(' / ')}`),
                                            ].filter(Boolean).join('\n')}>
                                              <Chip
                                                label={whExpiry.status === 'expired' && expiredUnits > 0
                                                  ? `${formatNumber(expiredUnits)} expired/on hold`
                                                  : `${whExpiry.status === 'soon' ? 'Expires' : 'Exp'} ${formatDate(whExpiry.earliestDate)}`}
                                                size="small"
                                                color={EXPIRY_STATUS_STYLE[whExpiry.status].color}
                                                variant={whExpiry.status === 'ok' ? 'outlined' : 'filled'}
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                              />
                                            </Tooltip>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Tooltip title="Click to see movement breakdown">
                                          <Typography
                                            fontWeight="medium"
                                            sx={{
                                              cursor: 'pointer',
                                              '&:hover': {
                                                color: 'primary.main',
                                                textDecoration: 'underline'
                                              }
                                            }}
                                            onClick={() => setMovementPopup({
                                              open: true,
                                              sku: primarySku,
                                              productName: item.product_name || primarySku,
                                              warehouseCode: wh.warehouse_code,
                                              warehouseData: wh
                                            })}
                                          >
                                            {formatNumber(wh.quantity)}
                                          </Typography>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell align="right">
                                        {Number.isFinite(wh.warehouse_reported_qty) ? (
                                          <Tooltip
                                            arrow
                                            placement="top"
                                            title={
                                              <Box sx={{ p: 0.5 }}>
                                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                  {wh.warehouse_reported_source} reported {formatNumber(wh.warehouse_reported_qty)} on-hand (as of {wh.warehouse_reported_asof})
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                  Available to sell: {formatNumber(wh.warehouse_reported_available)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                  Our calculated: {formatNumber(wh.quantity)} · Δ {wh.warehouse_reported_variance > 0 ? '+' : ''}{formatNumber(wh.warehouse_reported_variance)} (calc − reported)
                                                </Typography>
                                              </Box>
                                            }
                                          >
                                            <Typography
                                              component="span"
                                              sx={{
                                                cursor: 'help',
                                                fontWeight: 'medium',
                                                color: wh.warehouse_reported_variance === 0 ? 'success.main' : 'warning.main',
                                                textDecoration: 'underline dotted',
                                                textUnderlineOffset: '3px'
                                              }}
                                            >
                                              {formatNumber(wh.warehouse_reported_qty)}
                                              {wh.warehouse_reported_variance !== 0 && (
                                                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                                                  (Δ {wh.warehouse_reported_variance > 0 ? '+' : ''}{formatNumber(wh.warehouse_reported_variance)})
                                                </Typography>
                                              )}
                                            </Typography>
                                          </Tooltip>
                                        ) : (
                                          <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {Number.isFinite(wh.last_count_variance) ? (
                                          <Tooltip
                                            arrow
                                            placement="top"
                                            title={
                                              <Box sx={{ p: 0.5 }}>
                                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                  Last count {wh.last_stock_count_date}: reported {formatNumber(wh.last_reported_stock)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                  Calculated {formatNumber(wh.last_count_variance_system_qty)} (as of {wh.last_count_variance_asof_date})
                                                </Typography>
                                              </Box>
                                            }
                                          >
                                            <Typography
                                              component="span"
                                              sx={{
                                                cursor: 'help',
                                                fontWeight: 'medium',
                                                color: wh.last_count_variance === 0 ? 'success.main' : 'warning.main',
                                                textDecoration: 'underline dotted',
                                                textUnderlineOffset: '3px'
                                              }}
                                            >
                                              {wh.last_count_variance > 0 ? '+' : ''}{formatNumber(wh.last_count_variance)}
                                            </Typography>
                                          </Tooltip>
                                        ) : (
                                          <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color: warehouseSold90d > 0 && wh.quantity < warehouseSold90d ? 'warning.main' : 'inherit',
                                            fontWeight: warehouseSold90d > 0 && wh.quantity < warehouseSold90d ? 'bold' : 'normal'
                                          }}
                                        >
                                          {formatNumber(warehouseSold90d)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {(wh.incoming_stock || 0) > 0 ? (
                                          <Tooltip
                                            arrow
                                            placement="top"
                                            // A row left expanded across a brand/date change has an emptied cache: read it again on hover
                                            onOpen={() => loadIncomingDetails(primarySku)}
                                            title={(() => {
                                              const lines = incomingDetails[primarySku]?.[wh.warehouse_code];
                                              if (loadingIncoming[primarySku] && !lines) return 'Loading inbound details…';
                                              if (!lines || lines.length === 0) return 'No inbound breakdown available';
                                              return (
                                                <Box sx={{ p: 0.5 }}>
                                                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                                    Inbound to {wh.warehouse_code}
                                                  </Typography>
                                                  {lines.map((ln, lIdx) => {
                                                    const etaClean = ln.eta && ln.eta !== '2099-12-31' ? ln.eta : null;
                                                    return (
                                                      <Typography key={lIdx} variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                        {ln.source_type}{ln.reference ? ` ${ln.reference}` : ''}: {formatNumber(ln.quantity)} units
                                                        {ln.origin ? ` from ${ln.origin}` : ''}
                                                        {etaClean ? ` · ETA ${etaClean}` : ''}
                                                      </Typography>
                                                    );
                                                  })}
                                                </Box>
                                              );
                                            })()}
                                          >
                                            <Typography
                                              component="span"
                                              sx={{
                                                color: 'success.main',
                                                fontWeight: 'medium',
                                                cursor: 'help',
                                                textDecoration: 'underline dotted',
                                                textUnderlineOffset: '3px'
                                              }}
                                            >
                                              {formatNumber(wh.incoming_stock)}
                                            </Typography>
                                          </Tooltip>
                                        ) : (
                                          <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {wh.avg_unit_cost ? formatCurrency(wh.avg_unit_cost) : '—'}
                                      </TableCell>
                                      <TableCell align="right">
                                        {wh.value ? formatCurrency(wh.value) : '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Tooltip
                                          title={
                                            warehouseBatches.length > 0
                                              ? (
                                                <Box sx={{ p: 0.5 }}>
                                                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                                    Batches at {wh.warehouse_code}
                                                  </Typography>
                                                  {warehouseBatches.map((batch, bIdx) => {
                                                    return (
                                                      <Typography
                                                        key={bIdx}
                                                        variant="caption"
                                                        sx={{
                                                          display: 'block',
                                                          whiteSpace: 'nowrap',
                                                          color: batch.is_expired ? '#f44336' : batch.is_expiring ? '#ff9800' : 'inherit',
                                                          fontWeight: (batch.is_expired || batch.is_expiring) ? 'bold' : 'normal'
                                                        }}
                                                      >
                                                        {batch.batch_number}: {formatNumber(batch.current_quantity)} units
                                                        {batch.has_expiry && batch.expiry_date && ` (exp: ${batch.expiry_date})`}
                                                        {batch.is_expired && ' ⚠️ EXPIRED'}
                                                        {batch.is_expiring && ' ⏰ EXPIRING'}
                                                      </Typography>
                                                    );
                                                  })}
                                                </Box>
                                              )
                                              : 'No batch details loaded'
                                          }
                                          arrow
                                          placement="top"
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                            <Typography
                                              sx={{
                                                cursor: 'help',
                                                '&:hover': { color: 'primary.main' },
                                                color: hasExpiredBatches ? 'error.main' : hasExpiringBatches ? '#ff9800' : 'inherit',
                                                fontWeight: (hasExpiredBatches || hasExpiringBatches) ? 'bold' : 'normal'
                                              }}
                                            >
                                              {warehouseBatches.length}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={wh.is_external ? 'External' : 'Internal'}
                                          size="small"
                                          color={wh.is_external ? 'info' : 'success'}
                                          variant="filled"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Tooltip title="Edit warehouse stock">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openEditWarehouseDialog(wh, primarySku, item);
                                            }}
                                            sx={{
                                              p: 0.5,
                                              '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                {/* Total Row */}
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                  <TableCell>
                                    <Typography fontWeight="bold">Total</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="bold">
                                      {formatNumber(item.total_quantity)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {(() => {
                                      const fedWhs = (item.warehouses || []).filter(wh => Number.isFinite(wh.warehouse_reported_qty));
                                      if (fedWhs.length === 0) return <Typography fontWeight="bold" sx={{ color: 'text.secondary' }}>—</Typography>;
                                      const totalReported = fedWhs.reduce((sum, wh) => sum + (wh.warehouse_reported_qty || 0), 0);
                                      return <Typography fontWeight="bold">{formatNumber(totalReported)}</Typography>;
                                    })()}
                                  </TableCell>
                                  <TableCell align="right" />
                                  <TableCell align="right">
                                    <Typography fontWeight="bold">
                                      {formatNumber(item.sold_90d || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="bold" sx={{ color: 'success.main' }}>
                                      {formatNumber((item.warehouses || []).reduce((sum, wh) => sum + (wh.incoming_stock || 0), 0))}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="bold">
                                      {item.total_quantity > 0 ? formatCurrency(item.avg_unit_cost || 0) : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {(Number(item.total_value) || 0) === 0 && (Number(item.total_quantity) || 0) > 0 ? (
                                      <Typography fontWeight="bold" sx={{ color: 'warning.dark', fontStyle: 'italic', fontSize: 13 }}
                                        title="No COGS configured for this SKU">
                                        no cost
                                      </Typography>
                                    ) : (
                                      <Typography fontWeight="bold">
                                        {formatCurrency(item.total_value)}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {(item.warehouses || []).reduce((sum, wh) => sum + (wh.batch_count || 0), 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>

                            {/* Batch Details (loaded on demand) */}
                            {batchDetails[primarySku] && batchDetails[primarySku].length > 0 && (
                              <>
                                <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mt: 2 }}>
                                  Batch Inventory
                                </Typography>
                                <Table size="small" sx={{ mb: 2 }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell>Batch #</TableCell>
                                      <TableCell>Warehouse</TableCell>
                                      <TableCell>Date</TableCell>
                                      <TableCell align="right">Qty</TableCell>
                                      <TableCell align="right">Adjustments</TableCell>
                                      <TableCell align="right">Remaining</TableCell>
                                      <TableCell align="right">Unit Cost</TableCell>
                                      <TableCell>Expiry</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {batchDetails[primarySku].map((batch, idx) => (
                                      <TableRow
                                        key={batch.id || idx}
                                        hover
                                        sx={batch.is_expired ? {
                                          backgroundColor: 'error.50',
                                          opacity: 0.8
                                        } : {}}
                                      >
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            sx={batch.is_expired ? { textDecoration: 'line-through', color: 'text.disabled' } : {}}
                                          >
                                            {batch.batch_number}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{batch.warehouse_code}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                            {batch.receipt_date ? new Date(batch.receipt_date).toLocaleDateString() : '—'}
                                          </Typography>
                                          {batch.source === 'physical_count' && (
                                            <Typography variant="caption" color="text.disabled">Counted</Typography>
                                          )}
                                          {batch.source === 'warehouse_transfer' && (
                                            <Typography variant="caption" color="text.disabled" display="block">
                                              Transferred{batch.transfer_numbers?.length ? ` (${batch.transfer_numbers.join(', ')})` : ''}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" color="text.secondary">
                                            {formatNumber(batch.initial_quantity)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          {batch.adjustment_quantity !== 0 && batch.adjustment_quantity !== undefined ? (
                                            <Tooltip title="Manual adjustments applied to this batch">
                                              <Typography
                                                variant="body2"
                                                color={batch.adjustment_quantity > 0 ? 'success.main' : 'error.main'}
                                                fontWeight={500}
                                              >
                                                {batch.adjustment_quantity > 0 ? '+' : ''}{formatNumber(batch.adjustment_quantity)}
                                              </Typography>
                                            </Tooltip>
                                          ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color={batch.is_expired ? 'error.main' : batch.is_expiring ? '#ed6c02' : 'text.primary'}
                                          >
                                            {formatNumber(batch.current_quantity)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(batch.unit_cost)}</TableCell>
                                        <TableCell>
                                          {batch.has_expiry && batch.expiry_date ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <span style={{ color: batch.is_expired ? '#d32f2f' : batch.is_expiring ? '#ed6c02' : 'inherit', fontWeight: (batch.is_expired || batch.is_expiring) ? 600 : 400 }}>
                                                {formatDate(batch.expiry_date)}
                                              </span>
                                              {batch.is_expired ? (
                                                <Tooltip title="Batch has expired">
                                                  <WarningIcon fontSize="small" color="error" />
                                                </Tooltip>
                                              ) : batch.is_expiring ? (
                                                <Tooltip title={`Expires in ${batch.days_until_expiry} days`}>
                                                  <WarningIcon fontSize="small" color="warning" />
                                                </Tooltip>
                                              ) : null}
                                            </Box>
                                          ) : '—'}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={batch.is_expired ? 'Expired' : batch.is_expiring ? 'Expiring' : (batch.status || 'available')}
                                            size="small"
                                            color={batch.is_expired ? 'error' : batch.status === 'available' ? 'success' : 'default'}
                                            variant={batch.is_expired || batch.is_expiring ? 'filled' : 'outlined'}
                                            sx={batch.is_expiring ? { backgroundColor: '#ff9800', color: 'white' } : {}}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              setBatchMenuAnchor(e.currentTarget);
                                              setBatchMenuContext({ batch, primarySku });
                                            }}
                                          >
                                            <MoreVertIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </>
                            )}

                            {!loadingBatches[primarySku] && batchErrors[primarySku] && (
                              <Alert
                                severity="warning"
                                // As wide as its text: the panel sits in the stock table's horizontal scroll, so a
                                // full-width Alert put Retry past the visible edge at laptop widths (DT-894)
                                sx={{ my: 2, width: 'fit-content', maxWidth: '100%' }}
                                action={
                                  <Button color="inherit" size="small" onClick={() => loadBatchDetails(primarySku, null, true)}>
                                    Retry
                                  </Button>
                                }
                              >
                                {batchErrors[primarySku]}
                              </Alert>
                            )}

                            {loadingBatches[primarySku] && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Loading batch details...
                                </Typography>
                              </Box>
                            )}

                            {/* Child SKUs / Aliases */}
                            {item.child_skus && item.child_skus.filter(s => s !== primarySku).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                  SKU Aliases
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {item.child_skus.filter(s => s !== primarySku).map(sku => (
                                    <Chip key={sku} label={sku} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Identifiers */}
                            {((item.gtins && item.gtins.length > 0) || (item.asins && item.asins.length > 0)) && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                                  Product Identifiers
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {(item.gtins || []).map(gtin => (
                                    <Chip key={gtin} label={`GTIN: ${gtin}`} size="small" variant="outlined" />
                                  ))}
                                  {(item.asins || []).map(asin => (
                                    <Chip key={asin} label={`ASIN: ${asin}`} size="small" variant="outlined" color="primary" />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* View Movement History Button */}
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<SearchIcon />}
                              onClick={() => navigate(`/inventory/movements?sku=${primarySku}${selectedBrand?.id ? `&brand=${selectedBrand.id}` : ''}`)}
                              sx={{ mt: 2 }}
                            >
                              View Movement History
                            </Button>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedStock.length}
          page={page}
          onPageChange={(e, newPage) => { setPage(newPage); updateUrlParams({ page: newPage }); }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newRows = parseInt(e.target.value, 10);
            setRowsPerPage(newRows);
            setPage(0);
            updateUrlParams({ rows: newRows, page: 0 });
          }}
          rowsPerPageOptions={[25, 50, 100, 250, 500]}
        />
      </Card>

      {/* Export Options Dialog — choose exactly what to export */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{exportMode === 'detailed' ? 'Detailed Export' : 'Export Stock Levels'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tick as many as you like in each box (leave a box empty for "all"). The export runs in the background so the page stays usable. Last-count variance is off by default because it's slow — turn it on only if you need those columns.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Product Level</InputLabel>
              <Select
                multiple
                displayEmpty
                value={exportLevel}
                label="Product Level"
                onChange={(e) => setExportLevel(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(sel) => sel.length === 0 ? 'All levels' : sel.map(v => v.toUpperCase()).join(', ')}
              >
                {[['l0', 'L0 — raw materials / parts'], ['l1', 'L1 — finished goods'], ['l2', 'L2 — bundles']].map(([val, lbl]) => (
                  <MenuItem key={val} value={val}>
                    <Checkbox size="small" checked={exportLevel.indexOf(val) > -1} />
                    <ListItemText primary={lbl} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Status</InputLabel>
              <Select
                multiple
                displayEmpty
                value={exportStatus}
                label="Status"
                onChange={(e) => setExportStatus(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(sel) => sel.length === 0 ? 'All' : sel.map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(', ')}
              >
                {[['active', 'Active'], ['inactive', 'Inactive']].map(([val, lbl]) => (
                  <MenuItem key={val} value={val}>
                    <Checkbox size="small" checked={exportStatus.indexOf(val) > -1} />
                    <ListItemText primary={lbl} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Stock</InputLabel>
              <Select value={exportInStock} label="Stock" onChange={(e) => setExportInStock(e.target.value)}>
                <MenuItem value="all">All (incl. 0 qty)</MenuItem>
                <MenuItem value="instock">In stock only (qty &gt; 0)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Warehouse</InputLabel>
              <Select
                multiple
                displayEmpty
                value={exportWarehouse}
                label="Warehouse"
                onChange={(e) => {
                  const v = e.target.value;
                  const arr = typeof v === 'string' ? v.split(',') : v;
                  // Clicking "All Warehouses" clears the specific picks (empty = all).
                  if (arr.indexOf('__ALL__') > -1) { setExportWarehouse([]); return; }
                  setExportWarehouse(arr);
                }}
                renderValue={(sel) => sel.length === 0 ? 'All Warehouses' : sel.join(', ')}
              >
                <MenuItem value="__ALL__">
                  <Checkbox size="small" checked={exportWarehouse.length === 0} />
                  <ListItemText primary="All Warehouses" />
                </MenuItem>
                {warehouses.map(wh => (
                  <MenuItem key={wh.code} value={wh.code}>
                    <Checkbox size="small" checked={exportWarehouse.indexOf(wh.code) > -1} />
                    <ListItemText primary={`${wh.code} - ${wh.name}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox size="small" checked={exportIncludeVariance} onChange={(e) => setExportIncludeVariance(e.target.checked)} />}
              label="Include last-count variance (slower)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportConfirm}>Export</Button>
        </DialogActions>
      </Dialog>

      {/* Receive Inventory Dialog */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Inventory</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="SKU"
              value={receiveForm.sku}
              onChange={(e) => setReceiveForm({ ...receiveForm, sku: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={receiveForm.warehouse_id}
                label="Warehouse"
                onChange={(e) => setReceiveForm({ ...receiveForm, warehouse_id: e.target.value })}
              >
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name} ({w.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Batch Number"
              value={receiveForm.batch_number}
              onChange={(e) => setReceiveForm({ ...receiveForm, batch_number: e.target.value })}
              placeholder="Auto-generated if empty"
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={receiveForm.quantity}
                  onChange={(e) => setReceiveForm({ ...receiveForm, quantity: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Unit Cost"
                  type="number"
                  value={receiveForm.unit_cost}
                  onChange={(e) => setReceiveForm({ ...receiveForm, unit_cost: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="Expiry Date"
              type="date"
              value={receiveForm.expiry_date}
              onChange={(e) => setReceiveForm({ ...receiveForm, expiry_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '2000-01-01' }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReceiveSubmit}
            disabled={receiving || !receiveForm.sku || !receiveForm.warehouse_id || !receiveForm.quantity}
          >
            {receiving ? 'Receiving…' : 'Receive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Inventory from CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload a CSV file to bulk import inventory batches. Download the template below to see the required format.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ mb: 3 }}
              fullWidth
            >
              Download CSV Template
            </Button>

            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Required columns: sku, warehouse_code, batch_number, quantity, unit_cost
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Optional: expiry_date, notes
              </Typography>
            </Box>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="inventory-file-upload"
            />
            <label htmlFor="inventory-file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                Select CSV File
              </Button>
            </label>

            {importFile && (
              <Alert severity="info">
                Selected file: {importFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setImportFile(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Movement Breakdown Popup - Using reusable StockBreakdownDialog component */}
      <StockBreakdownDialog
        open={movementPopup.open}
        onClose={() => setMovementPopup({ ...movementPopup, open: false })}
        sku={movementPopup.sku}
        warehouseCode={movementPopup.warehouseCode}
        productName={movementPopup.productName}
        warehouseData={movementPopup.warehouseData}
        brandId={selectedBrand?.id}
        asOfDate={asOfDate}
      />

      {/* Batch Adjustment Dialog */}
      <Dialog
        open={adjustmentDialogOpen}
        onClose={() => setAdjustmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Batch Quantity</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {adjustmentForm.batch && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Batch Info */}
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">SKU</Typography>
                    <Typography variant="body1" fontWeight="medium">{adjustmentForm.batch.sku}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Batch #</Typography>
                    <Typography variant="body1" fontWeight="medium">{adjustmentForm.batch.batch_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Warehouse</Typography>
                    <Typography variant="body1">{adjustmentForm.batch.warehouse_code}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Quantity</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {formatNumber(adjustmentForm.batch.current_quantity)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Adjustment Form */}
              <Alert severity="info" sx={{ mt: 1 }}>
                Enter a <strong>positive number</strong> to add stock (e.g., found items).
                Enter a <strong>negative number</strong> to remove stock (e.g., damaged/lost items).
              </Alert>

              <TextField
                label="Adjustment Quantity *"
                type="number"
                value={adjustmentForm.adjustment_quantity}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_quantity: e.target.value })}
                placeholder="e.g., -50 or +100"
                fullWidth
                helperText={
                  adjustmentForm.adjustment_quantity
                    ? `New quantity will be: ${formatNumber(
                        (adjustmentForm.batch.current_quantity || 0) + parseFloat(adjustmentForm.adjustment_quantity || 0)
                      )}`
                    : ''
                }
              />

              <FormControl fullWidth>
                <InputLabel>Adjustment Type</InputLabel>
                <Select
                  value={adjustmentForm.adjustment_type}
                  label="Adjustment Type"
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_type: e.target.value })}
                >
                  <MenuItem value="manual">Manual Correction</MenuItem>
                  <MenuItem value="damage">Damaged Goods</MenuItem>
                  <MenuItem value="loss">Inventory Loss</MenuItem>
                  <MenuItem value="found">Found Items</MenuItem>
                  <MenuItem value="expired">Expired Write-off</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Reason *"
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                placeholder="Describe the reason for this adjustment..."
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialogOpen(false)} disabled={adjustmentLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAdjustment}
            disabled={!adjustmentForm.adjustment_quantity || !adjustmentForm.reason || adjustmentLoading}
          >
            {adjustmentLoading ? <CircularProgress size={20} /> : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Batch Dialog */}
      <Dialog
        open={editBatchDialogOpen}
        onClose={() => setEditBatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Batch</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {editBatchForm.batch && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Batch Info */}
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">SKU</Typography>
                    <Typography variant="body1" fontWeight="medium">{editBatchForm.batch.sku}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Warehouse</Typography>
                    <Typography variant="body1">{editBatchForm.batch.warehouse_code}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Receipt Date</Typography>
                    <Typography variant="body1">{formatDate(editBatchForm.batch.receipt_date)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Quantity</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">{formatNumber(editBatchForm.batch.current_quantity)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Batch Info Fields */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Batch Information
              </Typography>
              <TextField
                label="Batch Number"
                value={editBatchForm.batch_number}
                onChange={(e) => setEditBatchForm({ ...editBatchForm, batch_number: e.target.value })}
                placeholder="e.g., LOT-2024-001"
                fullWidth
                size="small"
              />

              <TextField
                label="Expiry Date"
                type="date"
                value={editBatchForm.expiry_date}
                onChange={(e) => setEditBatchForm({ ...editBatchForm, expiry_date: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
              />

              <TextField
                label="Unit Cost (COGS)"
                type="number"
                value={editBatchForm.unit_cost}
                onChange={(e) => setEditBatchForm({ ...editBatchForm, unit_cost: e.target.value })}
                placeholder="Enter unit cost"
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={
                  editBatchForm.unit_cost && editBatchForm.batch?.unit_cost !== editBatchForm.unit_cost
                    ? `Default from last batch. Original: $${editBatchForm.batch?.unit_cost || 0}`
                    : "Cost of goods for this batch"
                }
              />

              {/* Quantity Adjustment Section */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Adjust Quantity (Optional)
              </Typography>
              <TextField
                label="New Quantity"
                type="number"
                value={editBatchForm.new_quantity}
                onChange={(e) => setEditBatchForm({ ...editBatchForm, new_quantity: e.target.value })}
                placeholder={`Current: ${formatNumber(editBatchForm.batch.current_quantity)}`}
                fullWidth
                size="small"
                helperText="Enter the actual quantity after stock count"
              />

              {/* Show calculated adjustment */}
              {editBatchForm.new_quantity !== '' && parseFloat(editBatchForm.new_quantity) !== editBatchForm.batch.current_quantity && (
                <Box sx={{ p: 1.5, bgcolor: parseFloat(editBatchForm.new_quantity) > editBatchForm.batch.current_quantity ? 'success.50' : 'error.50', borderRadius: 1, border: '1px solid', borderColor: parseFloat(editBatchForm.new_quantity) > editBatchForm.batch.current_quantity ? 'success.200' : 'error.200' }}>
                  <Typography variant="body2" color="text.secondary">
                    Adjustment to be recorded:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={parseFloat(editBatchForm.new_quantity) > editBatchForm.batch.current_quantity ? 'success.main' : 'error.main'}
                  >
                    {parseFloat(editBatchForm.new_quantity) > editBatchForm.batch.current_quantity ? '+' : ''}
                    {formatNumber(parseFloat(editBatchForm.new_quantity) - editBatchForm.batch.current_quantity)}
                  </Typography>
                </Box>
              )}

              <TextField
                label="Reason"
                value={editBatchForm.adjustment_reason}
                onChange={(e) => setEditBatchForm({ ...editBatchForm, adjustment_reason: e.target.value })}
                placeholder="e.g., Stock count, Damaged goods"
                fullWidth
                size="small"
                disabled={editBatchForm.new_quantity === '' || parseFloat(editBatchForm.new_quantity) === editBatchForm.batch.current_quantity}
                error={editBatchForm.new_quantity !== '' && parseFloat(editBatchForm.new_quantity) !== editBatchForm.batch.current_quantity && !editBatchForm.adjustment_reason}
                helperText={editBatchForm.new_quantity !== '' && parseFloat(editBatchForm.new_quantity) !== editBatchForm.batch.current_quantity && !editBatchForm.adjustment_reason ? 'Reason is required' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBatchDialogOpen(false)} disabled={editBatchLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEditBatch}
            disabled={editBatchLoading || (editBatchForm.new_quantity !== '' && parseFloat(editBatchForm.new_quantity) !== editBatchForm.batch?.current_quantity && !editBatchForm.adjustment_reason)}
          >
            {editBatchLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Actions Menu */}
      <Menu
        anchorEl={batchMenuAnchor}
        open={Boolean(batchMenuAnchor)}
        onClose={() => setBatchMenuAnchor(null)}
      >
        {/* "Other stock" is a synthetic aggregate pseudo-batch (id 0) with no real
            batch row — PUT /batch/0 rejects with a confusing 400, so disable Edit for it.
            Dispose stays enabled: it writes an inventory_disposals row that nets back by
            warehouse+sku (not batch_number), so it works correctly on the remainder. */}
        <MenuItem
          disabled={batchMenuContext.batch?.id === 0 || batchMenuContext.batch?.batch_number === 'Other stock'}
          onClick={() => {
            setBatchMenuAnchor(null);
            openEditBatchDialog(batchMenuContext.batch, batchMenuContext.primarySku);
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Edit Batch"
            secondary={batchMenuContext.batch?.source === 'warehouse_transfer'
              ? 'Transferred stock — its expiry comes from the original goods receipt'
              : ((batchMenuContext.batch?.id === 0 || batchMenuContext.batch?.batch_number === 'Other stock') ? 'Aggregate stock — no batch to edit' : undefined)}
          />
        </MenuItem>
        <MenuItem onClick={() => {
          setBatchMenuAnchor(null);
          openDisposeBatchDialog(batchMenuContext.batch, batchMenuContext.primarySku);
        }}>
          <ListItemIcon><DisposeIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Dispose Inventory</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dispose Batch Dialog */}
      <Dialog
        open={disposeBatchDialogOpen}
        onClose={() => setDisposeBatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dispose Inventory</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {disposeBatchForm.batch && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Batch Info */}
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">SKU</Typography>
                    <Typography variant="body1" fontWeight="medium">{disposeBatchForm.batch.sku}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Warehouse</Typography>
                    <Typography variant="body1">{disposeBatchForm.batch.warehouse_code}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Batch #</Typography>
                    <Typography variant="body1">{disposeBatchForm.batch.batch_number || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Available Quantity</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">{formatNumber(disposeBatchForm.batch.current_quantity)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Disposal Type</InputLabel>
                <Select
                  value={disposeBatchForm.disposal_type}
                  onChange={(e) => setDisposeBatchForm({ ...disposeBatchForm, disposal_type: e.target.value })}
                  label="Disposal Type"
                >
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="damaged">Damaged</MenuItem>
                  <MenuItem value="write_off">Write Off</MenuItem>
                  <MenuItem value="sample">Sample / Gift</MenuItem>
                  <MenuItem value="theft">Theft / Loss</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Quantity to Dispose"
                type="number"
                value={disposeBatchForm.quantity}
                onChange={(e) => setDisposeBatchForm({ ...disposeBatchForm, quantity: e.target.value })}
                placeholder={`Max: ${disposeBatchForm.batch.current_quantity}`}
                fullWidth
                size="small"
                required
                inputProps={{ min: 1, max: disposeBatchForm.batch.current_quantity }}
                error={disposeBatchForm.quantity !== '' && (parseInt(disposeBatchForm.quantity) <= 0 || parseInt(disposeBatchForm.quantity) > disposeBatchForm.batch.current_quantity)}
                helperText={
                  disposeBatchForm.quantity !== '' && parseInt(disposeBatchForm.quantity) > disposeBatchForm.batch.current_quantity
                    ? `Cannot exceed available quantity (${formatNumber(disposeBatchForm.batch.current_quantity)})`
                    : ''
                }
              />

              <TextField
                label="Disposal Date"
                type="date"
                value={disposeBatchForm.disposal_date}
                onChange={(e) => setDisposeBatchForm({ ...disposeBatchForm, disposal_date: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Reason / Notes"
                value={disposeBatchForm.reason}
                onChange={(e) => setDisposeBatchForm({ ...disposeBatchForm, reason: e.target.value })}
                placeholder="e.g., Damaged during shipping"
                fullWidth
                size="small"
                multiline
                rows={2}
              />

              {/* Preview */}
              {disposeBatchForm.quantity !== '' && parseInt(disposeBatchForm.quantity) > 0 && parseInt(disposeBatchForm.quantity) <= disposeBatchForm.batch.current_quantity && (
                <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="body2" color="text.secondary">
                    After disposal:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatNumber(disposeBatchForm.batch.current_quantity)} → {formatNumber(disposeBatchForm.batch.current_quantity - parseInt(disposeBatchForm.quantity))} ({`-${formatNumber(parseInt(disposeBatchForm.quantity))}`})
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposeBatchDialogOpen(false)} disabled={disposeBatchLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitDisposeBatch}
            disabled={disposeBatchLoading || !disposeBatchForm.quantity || parseInt(disposeBatchForm.quantity) <= 0 || parseInt(disposeBatchForm.quantity) > (disposeBatchForm.batch?.current_quantity || 0)}
          >
            {disposeBatchLoading ? <CircularProgress size={20} /> : 'Dispose'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Warehouse Stock Dialog */}
      <Dialog
        open={editWarehouseDialogOpen}
        onClose={() => setEditWarehouseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Warehouse Stock
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Warehouse Info */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">SKU</Typography>
                  <Typography variant="body1" fontWeight="medium">{editWarehouseForm.sku}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Warehouse</Typography>
                  <Typography variant="body1">{editWarehouseForm.warehouse_code}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Current Quantity</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatNumber(editWarehouseForm.warehouse_data?.quantity || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Current Avg Cost</Typography>
                  <Typography variant="body1">
                    {editWarehouseForm.warehouse_data?.avg_unit_cost
                      ? formatCurrency(editWarehouseForm.warehouse_data.avg_unit_cost)
                      : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Edit Fields */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Adjust Stock
            </Typography>

            <TextField
              label="Effective Date"
              type="date"
              value={editWarehouseForm.effective_date || ''}
              onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, effective_date: e.target.value })}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              helperText={editWarehouseForm.effective_date && editWarehouseForm.effective_date < new Date().toISOString().slice(0, 10)
                ? 'Historical date — will create an inventory adjustment at this date'
                : 'Today — will create a stock receipt'
              }
            />

            <TextField
              label="True Quantity"
              type="number"
              value={editWarehouseForm.new_quantity}
              onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, new_quantity: e.target.value })}
              placeholder={`Current: ${formatNumber(editWarehouseForm.warehouse_data?.quantity || 0)}`}
              fullWidth
              size="small"
              helperText="Enter the actual quantity at this date"
            />

            {/* Show calculated adjustment (today only — for historical, the backend calculates it) */}
            {!editWarehouseForm.effective_date || editWarehouseForm.effective_date >= new Date().toISOString().slice(0, 10) ? (
              editWarehouseForm.new_quantity !== '' && parseFloat(editWarehouseForm.new_quantity) !== (editWarehouseForm.warehouse_data?.quantity || 0) && (
                <Box sx={{
                  p: 1.5,
                  bgcolor: parseFloat(editWarehouseForm.new_quantity) > (editWarehouseForm.warehouse_data?.quantity || 0) ? 'success.50' : 'error.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: parseFloat(editWarehouseForm.new_quantity) > (editWarehouseForm.warehouse_data?.quantity || 0) ? 'success.200' : 'error.200'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Adjustment to be recorded:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={parseFloat(editWarehouseForm.new_quantity) > (editWarehouseForm.warehouse_data?.quantity || 0) ? 'success.main' : 'error.main'}
                  >
                    {parseFloat(editWarehouseForm.new_quantity) > (editWarehouseForm.warehouse_data?.quantity || 0) ? '+' : ''}
                    {formatNumber(parseFloat(editWarehouseForm.new_quantity) - (editWarehouseForm.warehouse_data?.quantity || 0))}
                  </Typography>
                </Box>
              )
            ) : (
              <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                <Typography variant="body2" color="text.secondary">
                  The system will calculate what stock was on {editWarehouseForm.effective_date} and record the difference as an adjustment.
                </Typography>
              </Box>
            )}

            {/* GRN-specific fields — only show for today's adjustments */}
            {(!editWarehouseForm.effective_date || editWarehouseForm.effective_date >= new Date().toISOString().slice(0, 10)) && (
              <>
                <TextField
                  label="Unit Cost (COGS)"
                  type="number"
                  value={editWarehouseForm.unit_cost}
                  onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, unit_cost: e.target.value })}
                  placeholder="Enter unit cost"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Cost per unit for this adjustment"
                />

                <TextField
                  label="Expiry Date (Optional)"
                  type="date"
                  value={editWarehouseForm.expiry_date}
                  onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, expiry_date: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '2000-01-01' }}
                />

                <TextField
                  label="Batch Number"
                  value={editWarehouseForm.batch_number}
                  onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, batch_number: e.target.value })}
                  placeholder="e.g., ADJ-20251216"
                  fullWidth
                  size="small"
                  helperText="Reference number for this adjustment"
                />
              </>
            )}

            <TextField
              label="Reason"
              value={editWarehouseForm.adjustment_reason}
              onChange={(e) => setEditWarehouseForm({ ...editWarehouseForm, adjustment_reason: e.target.value })}
              placeholder="e.g., Stock count adjustment, Physical inventory"
              fullWidth
              size="small"
              required
              error={!editWarehouseForm.adjustment_reason}
              helperText={!editWarehouseForm.adjustment_reason ? 'Reason is required' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWarehouseDialogOpen(false)} disabled={editWarehouseLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEditWarehouse}
            disabled={editWarehouseLoading || !editWarehouseForm.adjustment_reason}
          >
            {editWarehouseLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default StockLevels;
